import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { DashboardLayout } from '../components/Layout';

const DOC_TYPES = [
  { value: 'FT', label: 'Fatura' },
  { value: 'FR', label: 'Fatura Recibo' },
  { value: 'FS', label: 'Fatura Simplificada' },
  { value: 'NC', label: 'Nota de Crédito' },
  { value: 'ND', label: 'Nota de Débito' },
  { value: 'OR', label: 'Encomenda Cliente' },
  { value: 'ORC', label: 'Orçamento' },
  { value: 'PP', label: 'Fatura Pro-Forma' },
  { value: 'GT', label: 'Guia de Transporte' },
];

const FISCAL_ZONES = [
  'Continente Portugal', 'Açores', 'Madeira', 'Intracomunitário', 'Exportação',
];

export default function AdminBillingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { section } = useParams();

  const sectionMap = {
    artigos: { tab: 'articles' },
    iva: { tab: 'vatRates' },
    metodos: { tab: 'payMethods' },
    recibos: { tab: 'receipts' },
    avencas: { tab: 'avencas' },
    armazens: { tab: 'warehouse', subtab: 'whs' },
    compras: { tab: 'purchases' },
    vendas: { tab: 'sales' },
    stocks: { tab: 'stocks', subtab: 'stock' },
    familias: { tab: 'families' },
  };

  const searchParams = new URLSearchParams(location.search);
  const mappedSection = section ? sectionMap[section.toLowerCase()] : null;
  const urlTab = mappedSection?.tab || searchParams.get('tab');
  const urlSubtab = mappedSection?.subtab || searchParams.get('subtab');
  
  const [activeTab, setActiveTab] = useState(urlTab || 'overview');
  // Data
  const [invoices, setInvoices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [avencaTipos, setAvencaTipos] = useState([]);
  const [avencaClientes, setAvencaClientes] = useState([]);
  // Warehouse & Purchases data
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchases, setPurchases] = useState([]); // Purchase Invoices from suppliers
  const [warehouseStock, setWarehouseStock] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [warehouseStats, setWarehouseStats] = useState({});
  const [purchaseStats, setPurchaseStats] = useState({});
  // Article form
  const [articleFormTab, setArticleFormTab] = useState('dados');
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState(getEmptyArticleForm());
  const [articleNutrition, setArticleNutrition] = useState(null);
  const [articleSupplierPrices, setArticleSupplierPrices] = useState([]);
  const [articleWarehouseStocks, setArticleWarehouseStocks] = useState([]);
  const [selectedArticleWarehouseStockId, setSelectedArticleWarehouseStockId] = useState(null);
  const [showAiImageModal, setShowAiImageModal] = useState(false);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [aiImageSuggestions, setAiImageSuggestions] = useState([]);
  const [aiImageQuery, setAiImageQuery] = useState('');
  const [showAiNutritionModal, setShowAiNutritionModal] = useState(false);
  const [aiNutritionLoading, setAiNutritionLoading] = useState(false);
  const [aiNutritionResult, setAiNutritionResult] = useState(null);
  const [articleLots, setArticleLots] = useState([]);
  const [articleSerials, setArticleSerials] = useState([]);
  const [articleImageFile, setArticleImageFile] = useState(null);
  // Invoice creation
  const [invoiceForm, setInvoiceForm] = useState(getEmptyInvoiceForm());
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  // VAT / Payment inline forms
  const [showVatForm, setShowVatForm] = useState(false);
  const [editingVatRate, setEditingVatRate] = useState(null);
  const [vatForm, setVatForm] = useState({ code: '', description: '', name: '', percentage: 23, country: 'PT', fiscalZone: 'Continente Portugal', type: 'Normal', isDefault: false })
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ code: '', name: '', isDefault: false, sortOrder: 0 });
  // Modals
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  // Print Options
  const [printOptions, setPrintOptions] = useState({
    copies: 1,
    layoutId: '',
    printer: '',
    doPrint: true,
    sendEmail: false,
    exportPdf: false,
    sendSms: false
  });
  // Receipt emit
  const [emitClientSearch, setEmitClientSearch] = useState('');
  const [emitClient, setEmitClient] = useState(null);
  const [emitInvoices, setEmitInvoices] = useState([]);
  const [emitSelectedInvoices, setEmitSelectedInvoices] = useState([]);
  const [emitPaymentMethod, setEmitPaymentMethod] = useState('');
  const [emitNotes, setEmitNotes] = useState('');
  const [emitAmounts, setEmitAmounts] = useState({});
  // Layout
  const [layoutForm, setLayoutForm] = useState(null);
  const [layoutTab, setLayoutTab] = useState('objetos');
  const [showLayoutDesigner, setShowLayoutDesigner] = useState(false);
  // Document Preview
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  // Print Queue
  const [printQueue, setPrintQueue] = useState([]);
  const [showPrintQueue, setShowPrintQueue] = useState(false);
  // Avencas
  const [showAvencaForm, setShowAvencaForm] = useState(false);
  const [avencaForm, setAvencaForm] = useState(getEmptyAvencaForm());
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(new Date().toISOString().slice(0, 7));
  // Warehouse forms
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState({ name: '', code: '', address: '', city: '', postalCode: '', phone: '', isDefault: false, allowNegativeStock: false, nãotes: '' });
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [showFamilyForm, setShowFamilyForm] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [familyForm, setFamilyForm] = useState({ code: '', name: '', description: '', parentId: null });
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ articleId: '', reference: '', quantity: 0, reason: '' });
  const [adjustWarehouseId, setAdjustWarehouseId] = useState(null);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferForm, setTransferForm] = useState({ articleId: '', reference: '', fromWarehouseId: '', toWarehouseId: '', quantity: 0, nãotes: '' });
  const [warehouseSubTab, setWarehouseSubTab] = useState('whs');

  useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab);
    }

    if (urlTab === 'purchases' && urlSubtab) {
      setPurchaseSubTab(urlSubtab);
    }

    if (urlTab === 'warehouse' && urlSubtab) {
      setWarehouseSubTab(urlSubtab);
    }

    if (urlTab === 'stocks' && (!urlSubtab || (urlSubtab !== 'stock' && urlSubtab !== 'mov'))) {
      setWarehouseSubTab('stock');
    }
  }, [urlTab, urlSubtab]);

  useEffect(() => {
    if (activeTab === 'stocks' && warehouseSubTab !== 'stock' && warehouseSubTab !== 'mov') {
      setWarehouseSubTab('stock');
    }
  }, [activeTab, warehouseSubTab]);

  useEffect(() => {
    if (activeTab === 'articles') {
      setSearchArticle('');
    }
  }, [activeTab]);
  // Supplier forms
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState(emptySupplier());
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState(emptyOrder());
  const [orderLines, setOrderLines] = useState([]);
  const [showReceiveForm, setShowReceiveForm] = useState(false);
  const [receivingOrder, setReceivingOrder] = useState(null);
  const [receiveLines, setReceiveLines] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [purchaseSubTab, setPurchaseSubTab] = useState(urlSubtab || 'suppliers');
  // Purchase Invoice forms
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState(emptyPurchase());
  const [purchaseLines, setPurchaseLines] = useState([]);
  const purchaseTrackingResolverRef = useRef(null);
  const purchaseSerialInputRefs = useRef([]);
  const [purchaseTrackingModal, setPurchaseTrackingModal] = useState({
    open: false,
    lineName: '',
    quantity: 0,
    lotEnabled: false,
    serialEnabled: false,
    lotNumber: '',
    expiryDate: '',
    serialValues: []
  });
  const [showArticleSearch, setShowArticleSearch] = useState(false);
  const [searchingLineIndex, setSearchingLineIndex] = useState(null);
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  // Lot form
  const [showLotForm, setShowLotForm] = useState(false);
  const [lotForm, setLotForm] = useState({ lotNumber: '', expiryDate: '', manufactureDate: '', quantity: 0, warehouseId: '', purchasePrice: 0, supplierId: '', nãotes: '' });
  const [serialForm, setSerialForm] = useState({ serialNumber: '', lotId: '', warehouseId: '', supplierId: '', notes: '' });
  const [searchArticle, setSearchArticle] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [receiptForm, setReceiptForm] = useState({ invoiceId: '', amount: '', paymentMethodId: '', date: new Date().toISOString().slice(0, 10), nãotes: '' });

  function getEmptyArticleForm() {
    // Match ArticleDto exactly - removed: name, stockControl, currentStock, minimumStock, maximumStock, reorderPoint, location, costPriceNoVat, costPriceWithVat, averageCostPrice, lastPurchasePrice
    return { 
      reference: '', 
      description: '', 
      shortDescription: '', 
      articleType: 'Produto', 
      category: '', 
      brand: '',
      barcode: '', 
      familyId: '', 
      unit: 'UN', 
      capacity: 0, 
      vatRateId: '', 
      alternativeVatRateId: '', 
      calculateProfitTax: false, 
      purchasePriceNoVat: 0, 
      purchasePriceWithVat: 0, 
      salePriceNoVat: 0, 
      salePriceWithVat: 0, 
      salePrice2NoVat: 0, 
      salePrice2WithVat: 0, 
      salePrice3NoVat: 0, 
      salePrice3WithVat: 0, 
      salePrice4NoVat: 0, 
      salePrice4WithVat: 0, 
      salePrice5NoVat: 0, 
      salePrice5WithVat: 0, 
      margin1: 0, 
      margin2: 0, 
      margin3: 0, 
      margin4: 0, 
      margin5: 0, 
      commissionPercent: 0, 
      askPriceWhenZero: true, 
      fixedCostPrice: false, 
      stock: 0, 
      customerOrderQty: 0,
      supplierOrderQty: 0,
      sellableStock: 0,
      controlsStock: false, 
      stockType: 'M', 
      isDiscontinued: false, 
      isResource: false, 
      imageUrl: '', 
      isActive: true, 
      lotControl: false, 
      lotStrategy: 'FIFO',
      serialNumberControl: false,
    };
  }
  function getEmptyNutritionInfo() {
    return {
      servingSize: 0,
      servingUnit: 'g',
      servingsPerPackage: 0,
      servingDescription: '',
      energyKcal: 0,
      energyKj: 0,
      fat: 0,
      saturatedFat: 0,
      carbohydrates: 0,
      sugars: 0,
      fiber: 0,
      protein: 0,
      salt: 0,
      energyKcal100g: 0,
      fat100g: 0,
      saturatedFat100g: 0,
      carbohydrates100g: 0,
      sugars100g: 0,
      fiber100g: 0,
      protein100g: 0,
      salt100g: 0,
      netWeight: 0,
      ingredients: '',
      allergens: '',
      allergenWarnings: '',
      storageInstructions: '',
      usageInstructions: '',
    };
  }
  const toN = (v) => Number(v || 0);
  const fmtNut = (v, digits = 2) => Number(v || 0).toFixed(digits);
  const unwrapAiProxyUrl = (rawUrl) => {
    const u = String(rawUrl || '').trim();
    if (!u) return '';
    // If backend returns our proxy endpoint (same-origin) but the server cannot fetch external
    // images, browsers should load the direct URL instead.
    if (u.includes('/api/billing/articles/ai/image-proxy')) {
      try {
        const parsed = new URL(u, window.location.origin);
        const inner = parsed.searchParams.get('url');
        if (inner) return inner; // URLSearchParams already decodes
      } catch {}
    }
    return u;
  };
  const pickAiImageUrls = (img) => {
    const full = unwrapAiProxyUrl(img?.url || img?.proxyUrl || '');
    const thumb = unwrapAiProxyUrl(img?.thumbUrl || img?.proxyThumbUrl || full);
    return { full, thumb };
  };
  const calcNutDr = (key, value) => {
    const ref = {
      fat: 70,
      saturatedFat: 20,
      carbohydrates: 260,
      sugars: 90,
      protein: 50,
      salt: 6,
      energyKcal: 2000,
    };
    const max = ref[key];
    if (!max) return '-';
    return `${Math.round((toN(value) / max) * 100)}%`;
  };
  function getEmptyInvoiceForm() {
    return { userId: '', documentType: 'FR', vendorName: '', fiscalZone: 'Continente Portugal', paymentTerms: 'Pronto Pagamento', paymentType: '', externalReference: '', dueDate: new Date().toISOString().slice(0, 10), nãotes: '', pricesIncludeTax: false, shippingCost: 0, globalDiscountPercent: 0, globalDiscountAmount: 0, retentionAmount: 0, roundingAmount: 0, clientPostalCode: '' };
  }
  function emptyPurchase() {
    return { documentType: 'Fatura Compra', invoiceNumber: '', supplierId: '', warehouseId: '', invoiceDate: new Date().toISOString().slice(0, 10), dueDate: '', supplierReference: '', nãotes: '', paymentTerms: 'Pronto Pagamento', paymentMethod: '', autoUpdateStock: true, globalDiscountPercent: 0 };
  }
  function getEmptyAvencaForm() {
    return { name: '', description: '', amount: 0, periodicity: 'Mensal', periodMonths: 1, vatRateId: '', articleId: '' };
  }
  function emptySupplier() {
    return { name: '', code: '', nif: '', address: '', postalCode: '', city: '', country: 'Portugal', phone: '', email: '', website: '', iban: '', nãotes: '', paymentTerms: '', defaultDiscount: 0 };
  }
  function emptyOrder() {
    return { supplierId: '', warehouseId: '', expectedDeliveryDate: '', supplierReference: '', nãotes: '', paymentTerms: '', globalDiscountPercent: 0 };
  }

  // ============================================
  // DATA LOADING
  // ============================================
  const loadAll = useCallback(async () => {
    try {
      const [invR, artR, vatR, payR, recR, layR, usrR, famR, avTR, avCR, whR, supR, poR, purR, whStatsR, puStatsR, movR] = await Promise.all([
        api.get('/billing/invoices').catch(() => ({ data: [] })),
        api.get('/billing/articles').catch(() => ({ data: [] })),
        api.get('/billing/vat-rates').catch(() => ({ data: [] })),
        api.get('/billing/payment-methods').catch(() => ({ data: [] })),
        api.get('/billing/receipts').catch(() => ({ data: [] })),
        api.get('/billing/layouts').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/warehouse/article-families').catch(() => ({ data: [] })),
        api.get('/billing/avencas/tipos').catch(() => ({ data: [] })),
        api.get('/billing/avencas/clientes').catch(() => ({ data: [] })),
        api.get('/warehouse').catch(() => ({ data: [] })),
        api.get('/purchases/suppliers').catch(() => ({ data: [] })),
        api.get('/purchases/orders').catch(() => ({ data: { orders: [] } })),
        api.get('/billing/purchases').catch(() => ({ data: { purchases: [] } })),
        api.get('/warehouse/stats').catch(() => ({ data: {} })),
        api.get('/purchases/stats').catch(() => ({ data: {} })),
        api.get('/warehouse/movements?pageSize=50').catch(() => ({ data: { movements: [] } })),
      ]);
      setInvoices(Array.isArray(invR.data) ? invR.data : []);
      setArticles(Array.isArray(artR.data) ? artR.data : []);
      setVatRates(Array.isArray(vatR.data) ? vatR.data : []);
      setPaymentMethods(Array.isArray(payR.data) ? payR.data : []);
      setReceipts(Array.isArray(recR.data) ? recR.data : []);
      setLayouts(Array.isArray(layR.data) ? layR.data : []);
      setUsers(Array.isArray(usrR.data) ? usrR.data : []);
      setFamilies(Array.isArray(famR.data) ? famR.data : []);
      setAvencaTipos(Array.isArray(avTR.data) ? avTR.data : []);
      setAvencaClientes(Array.isArray(avCR.data) ? avCR.data : []);
      setWarehouses(Array.isArray(whR.data) ? whR.data : []);
      setSuppliers(Array.isArray(supR.data) ? supR.data : []);
      const ordData = poR.data;
      setPurchaseOrders(Array.isArray(ordData?.orders) ? ordData.orders : Array.isArray(ordData) ? ordData : []);
      const purData = purR.data;
      setPurchases(Array.isArray(purData?.purchases) ? purData.purchases : Array.isArray(purData) ? purData : []);
      setWarehouseStats(whStatsR.data || {});
      setPurchaseStats(puStatsR.data || {});
      const movData = movR.data;
      setStockMovements(Array.isArray(movData?.movements) ? movData.movements : Array.isArray(movData) ? movData : []);
    } catch (e) { console.error('Erro ao carregar dados:', e); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ============================================
  // UTILITY
  // ============================================
  const fmtMoney = (v) => `${(Number(v) || 0).toFixed(2)} \u20AC`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '-';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-PT') : '-';
  const getClientCode = (user) => user?.codErp || user?.reference || `#${user?.id || 0}`;
  const getSupplierCode = (supplier) => supplier?.code || `SUP${String(supplier?.id || 0).padStart(6, '0')}`;

  // ============================================
  // INVOICE FUNCTIONS
  // ============================================
  const searchClientByCode = (code) => {
    if (!code.trim()) return;
    const normalizedCode = code.trim().toLowerCase();
    const client = users.find(u => String(getClientCode(u)).toLowerCase() === normalizedCode || u.id?.toString() === code.trim());
    if (client) {
      setInvoiceForm(f => ({ ...f, userId: client.id, clientPostalCode: client.postalCode || '' }));
      toast.success(`Cliente ${client.name} selecionado`);
      setClientSearchTerm('');
    } else {
      toast.warning('Cliente não encontrado. Abrindo pesquisa...');
      setClientSearchTerm(code);
      setShowClientSearch(true);
    }
  };

  const searchSupplierByCode = (code) => {
    if (!code || !code.trim()) {
      setSupplierSearchTerm('');
      setShowSupplierSearch(true);
      return;
    }
    
    // Procura primeiro pelo código exato
    const normalizedCode = code.trim().toLowerCase();
    const supplier = suppliers.find(s => 
      String(getSupplierCode(s)).toLowerCase() === normalizedCode || 
      s.id.toString() === code.trim()
    );
    
    if (supplier) {
      // Fornecedor encontrado - seleciona e preenche o código
      setPurchaseForm(f => ({ ...f, supplierId: supplier.id }));
      setSupplierSearchTerm(getSupplierCode(supplier));
      toast.success(`Fornecedor ${supplier.name} selecionado`);
    } else {
      // Não encontrado - abre modal com termo de pesquisa
      toast.warning('Código não encontrado. Abrindo pesquisa...');
      setSupplierSearchTerm(code);
      setShowSupplierSearch(true);
    }
  };

  const handleSearchUser = (term) => {
    setSearchUser(term);
    if (term.length < 2) { setFilteredUsers([]); return; }
    setFilteredUsers(users.filter(u => u.name?.toLowerCase().includes(term.toLowerCase()) || u.email?.toLowerCase().includes(term.toLowerCase()) || u.nif?.includes(term)));
  };

  const selectUser = (u) => {
    setInvoiceForm(f => ({ ...f, userId: u.id, clientPostalCode: u.postalCode || '' }));
    setSearchUser(u.name);
    setFilteredUsers([]);
  };

  const addInvoiceLine = () => {
    setInvoiceLines([...invoiceLines, { articleId: '', reference: 'Manual', description: '', quantity: 1, unitPrice: 0, discount: 0, vatRateId: vatRates.find(v => v.isDefault)?.id || '', warehouseId: '' }]);
  };

  const updateInvoiceLine = (idx, field, value) => {
    const lines = [...invoiceLines];
    lines[idx] = { ...lines[idx], [field]: value };
    if (field === 'articleId' && value) {
      const art = articles.find(a => a.id === parseInt(value));
      if (art) {
        lines[idx].reference = art.reference;
        lines[idx].description = art.description;
        lines[idx].unitPrice = art.salePriceNoVat;
        lines[idx].vatRateId = art.vatRateId || '';
      }
    }
    setInvoiceLines(lines);
  };

  const removeInvoiceLine = (idx) => setInvoiceLines(invoiceLines.filter((_, i) => i !== idx));

  const invoiceTotals = useMemo(() => {
    let totalBruto = 0, totalDesconto = 0, totalImposto = 0;
    invoiceLines.forEach(l => {
      const lineNet = l.quantity * l.unitPrice;
      const disc = lineNet * (l.discount || 0) / 100;
      const afterDisc = lineNet - disc;
      const vr = vatRates.find(v => v.id === parseInt(l.vatRateId));
      const tax = afterDisc * (vr?.percentage || 0) / 100;
      totalBruto += lineNet;
      totalDesconto += disc;
      totalImposto += tax;
    });
    const descGlobal = (totalBruto - totalDesconto) * (invoiceForm.globalDiscountPercent || 0) / 100;
    const totalLiquido = totalBruto - totalDesconto - descGlobal;
    const totalDoc = totalLiquido + totalImposto;
    const valorPagar = totalDoc - (invoiceForm.retentionAmount || 0) + (invoiceForm.roundingAmount || 0) + (invoiceForm.shippingCost || 0);
    return { totalBruto, totalDesconto, descGlobal, totalLiquido, totalImposto, totalDoc, valorPagar };
  }, [invoiceLines, vatRates, invoiceForm]);

  const createInvoice = async () => {
    if (!invoiceForm.userId) { toast.error('Selecione um cliente'); return; }
    if (invoiceLines.length === 0) { toast.error('Adicione pelo menos uma linha'); return; }
    try {
      const payload = {
        ...invoiceForm, userId: parseInt(invoiceForm.userId),
        paymentMethodId: invoiceForm.paymentType ? parseInt(invoiceForm.paymentType) : null,
        lines: invoiceLines.map(l => ({
          articleId: l.articleId ? parseInt(l.articleId) : null,
          reference: l.reference, description: l.description,
          quantity: parseFloat(l.quantity), unitPrice: parseFloat(l.unitPrice),
          discount: parseFloat(l.discount || 0),
          vatRateId: l.vatRateId ? parseInt(l.vatRateId) : null,
          warehouseId: l.warehouseId ? parseInt(l.warehouseId) : null,
        }))
      };
      const res = await api.post('/billing/invoices', payload);
      toast.success(`Documento ${res.data.invoiceNumber} criado!`);
      setPrintInvoice(res.data);
      // Set default layout for document type
      const defaultLayout = layouts.find(l => l.documentType === res.data.documentType && l.isDefault);
      setPrintOptions({
        copies: 1,
        layoutId: defaultLayout?.id || (layouts[0]?.id || ''),
        printer: 'HP LaserJet Professional M121nf MFP',
        doPrint: true,
        sendEmail: false,
        exportPdf: true,
        sendSms: false
      });
      setShowPrintModal(true);
      setInvoiceForm(getEmptyInvoiceForm());
      setInvoiceLines([]);
      setSearchUser('');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro ao criar documento'); }
  };

  const payInvoice = async () => {
    if (!payingInvoice) return;
    try {
      await api.post(`/billing/invoices/${payingInvoice.id}/pay`);
      toast.success('Fatura marcada como paga');
      setShowPayModal(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };

  const cancelInvoice = async (id) => {
    if (!window.confirm('Anular esta fatura?')) return;
    try { await api.post(`/billing/invoices/${id}/cancel`); toast.success('Fatura anulada'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };

  const printInvoiceAction = async (inv) => {
    try { const res = await api.get(`/billing/invoices/${inv.id}/pdf`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data])); window.open(url, '_blank'); }
    catch { toast.error('Erro ao gerar PDF'); }
  };

  const emailInvoiceAction = async (inv) => {
    try { await api.post(`/billing/invoices/${inv.id}/send-email`); toast.success('Email enviado'); }
    catch { toast.error('Erro ao enviar email'); }
  };
  // ============================================
  // ARTICLE FUNCTIONS
  // ============================================
  const openNewArticle = () => {
    setEditingArticle(null);
    setArticleForm(getEmptyArticleForm());
    setArticleNutrition(getEmptyNutritionInfo());
    setArticleSupplierPrices([]);
    setArticleWarehouseStocks([]);
    setSelectedArticleWarehouseStockId(null);
    setArticleLots([]);
    setArticleSerials([]);
    setArticleImageFile(null);
    setArticleFormTab('dados');
    setShowArticleForm(true);
  };

  const openEditArticle = async (art) => {
    setEditingArticle(art);
    setArticleForm({
      reference: art.reference, description: art.description, shortDescription: art.shortDescription || '',
      articleType: art.articleType || 'Produto', category: art.category || '', brand: art.brand || '', barcode: art.barcode || '',
      familyId: art.familyId || '', unit: art.unit || 'UN', capacity: art.capacity || 0,
      vatRateId: art.vatRateId || '', alternativeVatRateId: art.alternativeVatRateId || '',
      calculateProfitTax: art.calculateProfitTax || false,
      purchasePriceNoVat: art.purchasePriceNoVat || 0, purchasePriceWithVat: art.purchasePriceWithVat || 0,
      salePriceNoVat: art.salePriceNoVat || 0, salePriceWithVat: art.salePriceWithVat || 0,
      salePrice2NoVat: art.salePrice2NoVat || 0, salePrice2WithVat: art.salePrice2WithVat || 0,
      salePrice3NoVat: art.salePrice3NoVat || 0, salePrice3WithVat: art.salePrice3WithVat || 0,
      salePrice4NoVat: art.salePrice4NoVat || 0, salePrice4WithVat: art.salePrice4WithVat || 0,
      salePrice5NoVat: art.salePrice5NoVat || 0, salePrice5WithVat: art.salePrice5WithVat || 0,
      margin1: art.margin1 || 0, margin2: art.margin2 || 0, margin3: art.margin3 || 0,
      margin4: art.margin4 || 0, margin5: art.margin5 || 0,
      commissionPercent: art.commissionPercent || 0, askPriceWhenZero: art.askPriceWhenZero,
      fixedCostPrice: art.fixedCostPrice, stock: art.stock || 0,
      customerOrderQty: Number(art.customerOrderQty || 0),
      supplierOrderQty: Number(art.supplierOrderQty || 0),
      sellableStock: Number((art.sellableStock ?? ((art.stock || 0) - (art.customerOrderQty || 0))) || 0),
      controlsStock: art.controlsStock || art.stockControl || false, stockControl: art.stockControl || art.controlsStock || false, stockType: art.stockType || 'M',
      currentStock: art.currentStock || art.stock || 0, minimumStock: art.minimumStock || 0,
      maximumStock: art.maximumStock || 0, reorderPoint: art.reorderPoint || 0, location: art.location || '',
      costPriceNoVat: art.costPriceNoVat || 0, costPriceWithVat: art.costPriceWithVat || 0,
      averageCostPrice: art.averageCostPrice || 0, lastPurchasePrice: art.lastPurchasePrice || 0,
      isDiscontinued: art.isDiscontinued, isResource: art.isResource,
      imageUrl: art.imageUrl || '', isActive: art.isActive,
      lotControl: art.lotControl || false, lotStrategy: art.lotStrategy || 'FIFO',
      serialNumberControl: art.serialNumberControl || false,
    });
    setArticleFormTab('dados');
    setShowArticleForm(true);
    setArticleImageFile(null);
    // Load related data
    try {
      const [nutR, spR, stockR, lotR, serialR] = await Promise.all([
        api.get(`/billing/articles/${art.id}/nutrition`).catch(() => ({ data: null })),
        api.get(`/billing/articles/${art.id}/supplier-prices`).catch(() => ({ data: [] })),
        api.get(`/billing/articles/${art.id}/warehouse-stock`).catch(() => ({ data: [] })),
        api.get(`/billing/articles/${art.id}/lots`).catch(() => ({ data: [] })),
        api.get(`/billing/articles/${art.id}/serial-numbers`).catch(() => ({ data: [] })),
      ]);
      setArticleNutrition(nutR.data || getEmptyNutritionInfo());
      setArticleSupplierPrices(Array.isArray(spR.data) ? spR.data : []);
      const whStocks = Array.isArray(stockR.data) ? stockR.data : [];
      setArticleWarehouseStocks(whStocks);
      setSelectedArticleWarehouseStockId(whStocks.length > 0 ? whStocks[0].warehouseId : null);
      setArticleLots(Array.isArray(lotR.data) ? lotR.data : []);
      setArticleSerials(Array.isArray(serialR.data) ? serialR.data : []);
    } catch {}
  };

  const searchImageByAI = async () => {
    const familyName = families.find(f => Number(f.id) === Number(articleForm.familyId))?.name || '';
    const query = (articleForm.description || articleForm.shortDescription || articleForm.reference || '').trim();
    if (!query) {
      toast.warning('Preencha a descrição ou referência do artigo primeiro');
      return;
    }

    try {
      setAiImageLoading(true);
      const res = await api.post('/billing/articles/ai/image-suggestions', {
        query,
        family: familyName,
        brand: (articleForm.brand || '').trim(),
        barcode: (articleForm.barcode || '').trim(),
        count: 8
      });
      const imgs = Array.isArray(res.data?.images) ? res.data.images : [];
      setAiImageQuery(res.data?.suggestedQuery || [query, familyName].filter(Boolean).join(' '));
      setAiImageSuggestions(imgs);
      setShowAiImageModal(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao procurar imagem por IA');
    } finally {
      setAiImageLoading(false);
    }
  };

  const suggestNutritionByAI = async () => {
    const description = (articleForm.description || articleForm.shortDescription || articleForm.reference || '').trim();
    if (!description) {
      toast.warning('Preencha a descrição do artigo primeiro');
      return;
    }

    try {
      setAiNutritionLoading(true);
      const res = await api.post('/billing/articles/ai/nutrition-suggestion', {
        articleId: editingArticle?.id || null,
        description
      });
      setAiNutritionResult(res.data || null);
      setShowAiNutritionModal(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao obter sugestão nutricional por IA');
    } finally {
      setAiNutritionLoading(false);
    }
  };

  const applyAiNutritionSuggestion = () => {
    const suggestion = aiNutritionResult?.suggestion;
    if (!suggestion) return;
    setArticleNutrition(prev => ({ ...getEmptyNutritionInfo(), ...(prev || {}), ...suggestion }));
    setShowAiNutritionModal(false);
    toast.success('Sugestão nutricional aplicada. Pode rever antes de gravar.');
  };

  const saveArticle = async () => {
    if (!articleForm.reference || !articleForm.description) { toast.error('Referência e descrição obrigatórios'); return; }
    try {
      // Filter only valid ArticleDto fields - convert empty strings to null for numeric fields
      const validDto = {
        reference: articleForm.reference,
        description: articleForm.description,
        shortDescription: articleForm.shortDescription || null,
        articleType: articleForm.articleType,
        category: articleForm.category || null,
        brand: articleForm.brand || null,
        barcode: articleForm.barcode || null,
        familyId: articleForm.familyId ? parseInt(articleForm.familyId) : null,
        capacity: parseFloat(articleForm.capacity) || 0,
        unit: articleForm.unit || 'UN',
        vatRateId: articleForm.vatRateId ? parseInt(articleForm.vatRateId) : null,
        alternativeVatRateId: articleForm.alternativeVatRateId ? parseInt(articleForm.alternativeVatRateId) : null,
        calculateProfitTax: articleForm.calculateProfitTax || false,
        purchasePriceNoVat: articleForm.purchasePriceNoVat || 0,
        purchasePriceWithVat: articleForm.purchasePriceWithVat || 0,
        salePriceNoVat: articleForm.salePriceNoVat || 0,
        salePriceWithVat: articleForm.salePriceWithVat || 0,
        salePrice2NoVat: articleForm.salePrice2NoVat || 0,
        salePrice2WithVat: articleForm.salePrice2WithVat || 0,
        salePrice3NoVat: articleForm.salePrice3NoVat || 0,
        salePrice3WithVat: articleForm.salePrice3WithVat || 0,
        salePrice4NoVat: articleForm.salePrice4NoVat || 0,
        salePrice4WithVat: articleForm.salePrice4WithVat || 0,
        salePrice5NoVat: articleForm.salePrice5NoVat || 0,
        salePrice5WithVat: articleForm.salePrice5WithVat || 0,
        margin1: articleForm.margin1 || 0,
        margin2: articleForm.margin2 || 0,
        margin3: articleForm.margin3 || 0,
        margin4: articleForm.margin4 || 0,
        margin5: articleForm.margin5 || 0,
        commissionPercent: articleForm.commissionPercent || 0,
        askPriceWhenZero: articleForm.askPriceWhenZero !== false,
        fixedCostPrice: articleForm.fixedCostPrice || false,
        stock: Math.trunc(Number(articleForm.stock) || 0),
        controlsStock: !!(articleForm.controlsStock ?? articleForm.stockControl),
        stockType: articleForm.stockType || 'M',
        isDiscontinued: articleForm.isDiscontinued || false,
        isResource: articleForm.isResource || false,
        imageUrl: articleForm.imageUrl,
        lotControl: articleForm.lotControl || false,
        lotStrategy: articleForm.lotStrategy || 'FIFO',
        serialNumberControl: articleForm.serialNumberControl || false,
        isActive: articleForm.isActive !== false
      };
      
      if (editingArticle) {
        await api.put(`/billing/articles/${editingArticle.id}`, validDto);
        // Upload image if selected
        if (articleImageFile) {
          const fd = new FormData();
          fd.append('file', articleImageFile);
          await api.post(`/billing/articles/${editingArticle.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        // Save nutrition if on that tab
        if (articleNutrition) {
          await api.post(`/billing/articles/${editingArticle.id}/nutrition`, articleNutrition);
        }
        toast.success('Artigo atualizado');
      } else {
        const res = await api.post('/billing/articles', validDto);
        const newId = res.data?.article?.id || res.data?.id;
        if (articleImageFile && newId) {
          const fd = new FormData();
          fd.append('file', articleImageFile);
          await api.post(`/billing/articles/${newId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        if (articleNutrition && newId) {
          await api.post(`/billing/articles/${newId}/nutrition`, articleNutrition);
        }
        toast.success('Artigo criado');
      }
      setShowArticleForm(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro ao guardar artigo'); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Desativar este artigo?')) return;
    try { await api.delete(`/billing/articles/${id}`); toast.success('Artigo desativado'); loadAll(); }
    catch { toast.error('Erro'); }
  };

  const recalculateArticleStock = async () => {
    try {
      const res = await api.post('/billing/articles/recalculate-stock');
      toast.success(res.data?.message || 'Stock recalculado com sucesso');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao recalcular stock');
    }
  };

  // Price auto-calc: NoVat -> WithVat
  const calcPriceWithVat = (priceNoVat) => {
    const vr = vatRates.find(v => v.id === parseInt(articleForm.vatRateId));
    const pct = vr?.percentage || 0;
    return Math.round(priceNoVat * (1 + pct / 100) * 100) / 100;
  };
  const calcPriceNoVat = (priceWithVat) => {
    const vr = vatRates.find(v => v.id === parseInt(articleForm.vatRateId));
    const pct = vr?.percentage || 0;
    return Math.round(priceWithVat / (1 + pct / 100) * 100) / 100;
  };
  const calcMargin = (saleNoVat) => {
    const cost = articleForm.purchasePriceNoVat || 0;
    if (cost === 0) return 0;
    return Math.round((saleNoVat - cost) / cost * 100 * 100) / 100;
  };

  const handlePriceNoVatEnter = (level, value) => {
    const withVat = calcPriceWithVat(parseFloat(value) || 0);
    const margin = calcMargin(parseFloat(value) || 0);
    if (level === 1) setArticleForm(f => ({ ...f, salePriceNoVat: parseFloat(value) || 0, salePriceWithVat: withVat, margin1: margin }));
    else if (level === 2) setArticleForm(f => ({ ...f, salePrice2NoVat: parseFloat(value) || 0, salePrice2WithVat: withVat, margin2: margin }));
    else if (level === 3) setArticleForm(f => ({ ...f, salePrice3NoVat: parseFloat(value) || 0, salePrice3WithVat: withVat, margin3: margin }));
    else if (level === 4) setArticleForm(f => ({ ...f, salePrice4NoVat: parseFloat(value) || 0, salePrice4WithVat: withVat, margin4: margin }));
    else if (level === 5) setArticleForm(f => ({ ...f, salePrice5NoVat: parseFloat(value) || 0, salePrice5WithVat: withVat, margin5: margin }));
  };

  const handlePriceWithVatEnter = (level, value) => {
    const nãoVat = calcPriceNoVat(parseFloat(value) || 0);
    const margin = calcMargin(nãoVat);
    if (level === 1) setArticleForm(f => ({ ...f, salePriceWithVat: parseFloat(value) || 0, salePriceNoVat: nãoVat, margin1: margin }));
    else if (level === 2) setArticleForm(f => ({ ...f, salePrice2WithVat: parseFloat(value) || 0, salePrice2NoVat: nãoVat, margin2: margin }));
    else if (level === 3) setArticleForm(f => ({ ...f, salePrice3WithVat: parseFloat(value) || 0, salePrice3NoVat: nãoVat, margin3: margin }));
    else if (level === 4) setArticleForm(f => ({ ...f, salePrice4WithVat: parseFloat(value) || 0, salePrice4NoVat: nãoVat, margin4: margin }));
    else if (level === 5) setArticleForm(f => ({ ...f, salePrice5WithVat: parseFloat(value) || 0, salePrice5NoVat: nãoVat, margin5: margin }));
  };

  // Lot functions
  const saveLot = async () => {
    if (!editingArticle) return;
    try {
      await api.post(`/billing/articles/${editingArticle.id}/lots`, lotForm);
      toast.success('Lote criado');
      setShowLotForm(false);
      const res = await api.get(`/billing/articles/${editingArticle.id}/lots`);
      setArticleLots(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };

  const saveSerial = async () => {
    if (!editingArticle) return;
    if (!serialForm.serialNumber?.trim()) {
      toast.error('Número de Série / IMEI é obrigatório');
      return;
    }
    try {
      const payload = {
        serialNumber: serialForm.serialNumber.trim(),
        lotId: serialForm.lotId ? parseInt(serialForm.lotId) : null,
        warehouseId: serialForm.warehouseId ? parseInt(serialForm.warehouseId) : null,
        supplierId: serialForm.supplierId ? parseInt(serialForm.supplierId) : null,
        notes: serialForm.notes || null,
      };
      await api.post(`/billing/articles/${editingArticle.id}/serial-numbers`, payload);
      toast.success('Número de série criado');
      setSerialForm({ serialNumber: '', lotId: '', warehouseId: '', supplierId: '', notes: '' });
      const res = await api.get(`/billing/articles/${editingArticle.id}/serial-numbers`);
      setArticleSerials(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro');
    }
  };

  // ============================================
  // VAT / PAYMENT METHODS
  // ============================================
  const saveVatRate = async () => {
    try {
      if (editingVatRate) {
        await api.put(`/billing/vat-rates/${editingVatRate.id}`, vatForm);
        toast.success('Taxa atualizada');
      } else {
        await api.post('/billing/vat-rates', vatForm);
        toast.success('Taxa criada');
      }
      setShowVatForm(false);
      setEditingVatRate(null);
      setVatForm({ code: '', description: '', name: '', percentage: 23, country: 'PT', fiscalZone: 'Continente Portugal', type: 'Normal', isDefault: false });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const deleteVatRate = async (id) => {
    if (!window.confirm('Eliminar?')) return;
    try { await api.delete(`/billing/vat-rates/${id}`); toast.success('Eliminada'); loadAll(); }
    catch { toast.error('Erro'); }
  };
  const savePayMethod = async () => {
    try { await api.post('/billing/payment-methods', payForm); toast.success('Método criado'); setShowPayForm(false); setPayForm({ code: '', name: '', isDefault: false, sortOrder: 0 }); loadAll(); }
    catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const deletePayMethod = async (id) => {
    if (!window.confirm('Eliminar?')) return;
    try { await api.delete(`/billing/payment-methods/${id}`); toast.success('Eliminado'); loadAll(); }
    catch { toast.error('Erro'); }
  };

  // ============================================
  // AVENCAS
  // ============================================
  const saveAvenca = async () => {
    try {
      const periodicityMap = { Mensal: 'Monthly', Anual: 'Yearly' };
      const payload = {
        name: avencaForm.name || avencaForm.description || 'Avença',
        description: avencaForm.description || null,
        amount: parseFloat(avencaForm.amount || 0),
        vatRateId: avencaForm.vatRateId ? parseInt(avencaForm.vatRateId) : null,
        articleId: avencaForm.articleId ? parseInt(avencaForm.articleId) : null,
        periodicity: periodicityMap[avencaForm.periodicity] || 'Monthly',
        periodMonths: avencaForm.periodicity === 'Anual' ? 12 : 1,
        documentType: 'FT',
        isActive: true,
      };
      await api.post('/billing/avencas/tipos', payload);
      toast.success('Avença criada'); setShowAvencaForm(false); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const generateAvencas = async () => {
    try { const res = await api.post('/billing/avencas/gerar', { month: generateMonth }); toast.success(res.data?.message || 'Gerado'); setShowGenerateModal(false); loadAll(); }
    catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };

  // ============================================
  // RECEIPT EMIT
  // ============================================
  const searchEmitClient = async (term) => {
    setEmitClientSearch(term);
    if (term.length < 2) return;
    const found = users.find(u => u.name?.toLowerCase().includes(term.toLowerCase()) || u.email?.toLowerCase().includes(term.toLowerCase()));
    if (found) {
      setEmitClient(found);
      try { const res = await api.get(`/billing/clients/${found.id}/pending`); setEmitInvoices(Array.isArray(res.data) ? res.data : []); }
      catch { setEmitInvoices([]); }
    }
  };

  const emitReceipt = async () => {
    if (!emitClient || emitSelectedInvoices.length === 0) { toast.error('Selecione cliente e faturas'); return; }
    try {
      await api.post('/billing/receipts/emit', {
        clientId: emitClient.id,
        paymentMethodId: emitPaymentMethod ? parseInt(emitPaymentMethod) : null,
        nãotes: emitNotes,
        lines: emitSelectedInvoices.map(invId => ({ invoiceId: invId, amount: parseFloat(emitAmounts[invId] || 0) }))
      });
      toast.success('Recibo emitido!');
      setEmitClient(null); setEmitClientSearch(''); setEmitInvoices([]); setEmitSelectedInvoices([]); setEmitAmounts({}); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro ao emitir recibo'); }
  };
  // ============================================
  // WAREHOUSE FUNCTIONS
  // ============================================
  const loadWarehouseStock = async (id) => {
    try { const res = await api.get(`/warehouse/${id}/stock`); setWarehouseStock(Array.isArray(res.data) ? res.data : []); setSelectedWarehouse(warehouses.find(w => w.id === id)); }
    catch { toast.error('Erro ao carregar stock'); }
  };
  const saveWarehouse = async () => {
    try {
      if (editingWarehouse) { await api.put(`/warehouse/${editingWarehouse.id}`, warehouseForm); toast.success('Armazém atualizado'); }
      else { await api.post('/warehouse', warehouseForm); toast.success('Armazém criado'); }
      setShowWarehouseForm(false); setEditingWarehouse(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const editWarehouse = (w) => {
    setEditingWarehouse(w);
    setWarehouseForm({ name: w.name, code: w.code || '', address: w.address || '', city: w.city || '', postalCode: w.postalCode || '', phone: w.phone || '', isDefault: w.isDefault, allowNegativeStock: w.allowNegativeStock, nãotes: w.nãotes || '' });
    setShowWarehouseForm(true);
  };
  const saveFamily = async () => {
    try {
      if (editingFamily) {
        await api.put(`/warehouse/article-families/${editingFamily.id}`, familyForm);
        toast.success('Família atualizada');
      } else {
        await api.post('/warehouse/article-families', familyForm);
        toast.success('Família criada');
      }
      setShowFamilyForm(false);
      setEditingFamily(null);
      setFamilyForm({ code: '', name: '', description: '', parentId: null });
      loadAll();
    }
    catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const doAdjust = async () => {
    try { await api.post(`/warehouse/${adjustWarehouseId}/stock/adjust`, adjustForm); toast.success('Stock ajustado'); setShowAdjustForm(false); loadAll(); if (selectedWarehouse) loadWarehouseStock(selectedWarehouse.id); }
    catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const doTransfer = async () => {
    try { await api.post('/warehouse/transfer', transferForm); toast.success('Transferência efetuada'); setShowTransferForm(false); loadAll(); if (selectedWarehouse) loadWarehouseStock(selectedWarehouse.id); }
    catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };

  // ============================================
  // PURCHASE FUNCTIONS
  // ============================================
  const saveSupplier = async () => {
    try {
      if (editingSupplier) { await api.put(`/purchases/suppliers/${editingSupplier.id}`, supplierForm); toast.success('Fornecedor atualizado'); }
      else { await api.post('/purchases/suppliers', supplierForm); toast.success('Fornecedor criado'); }
      setShowSupplierForm(false); setEditingSupplier(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const editSupplierFn = (s) => {
    setEditingSupplier(s);
    setSupplierForm({ name: s.name, code: s.code || '', nif: s.nif || '', address: s.address || '', postalCode: s.postalCode || '', city: s.city || '', country: s.country || 'Portugal', phone: s.phone || '', email: s.email || '', website: s.website || '', iban: s.iban || '', nãotes: s.nãotes || '', paymentTerms: s.paymentTerms || '', defaultDiscount: s.defaultDiscount || 0 });
    setShowSupplierForm(true);
  };
  const addOrderLine = () => { setOrderLines([...orderLines, { articleId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, vatPercent: 23 }]); };
  const updateOrderLine = (idx, field, value) => {
    const lines = [...orderLines];
    lines[idx] = { ...lines[idx], [field]: value };
    if (field === 'articleId' && value) {
      const art = articles.find(a => a.id === parseInt(value));
      if (art) { lines[idx].description = art.description; lines[idx].unitPrice = art.purchasePriceNoVat; lines[idx].vatPercent = art.vatRate?.percentage || 23; }
    }
    setOrderLines(lines);
  };
  const saveOrder = async () => {
    try {
      const payload = { ...orderForm, supplierId: parseInt(orderForm.supplierId), warehouseId: orderForm.warehouseId ? parseInt(orderForm.warehouseId) : null, lines: orderLines.map(l => ({ ...l, articleId: parseInt(l.articleId), quantity: parseFloat(l.quantity), unitPrice: parseFloat(l.unitPrice), discountPercent: parseFloat(l.discountPercent || 0), vatPercent: parseFloat(l.vatPercent || 23) })) };
      await api.post('/purchases/orders', payload);
      toast.success('Encomenda criada'); setShowOrderForm(false); setOrderLines([]); setOrderForm(emptyOrder()); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const confirmOrder = async (id) => { try { await api.post(`/purchases/orders/${id}/confirm`); toast.success('Confirmada'); loadAll(); } catch (e) { toast.error(e.response?.data?.message || 'Erro'); } };
  const cancelOrderFn = async (id) => { if (!window.confirm('Cancelar?')) return; try { await api.post(`/purchases/orders/${id}/cancel`); toast.success('Cancelada'); loadAll(); } catch (e) { toast.error(e.response?.data?.message || 'Erro'); } };
  const openReceive = async (order) => {
    try {
      const res = await api.get(`/purchases/orders/${order.id}`);
      const fo = res.data;
      setReceivingOrder(fo);
      setReceiveLines((fo.lines || []).map(l => ({ lineId: l.id, quantity: l.quantity - l.receivedQuantity, description: l.description || l.article?.description, ordered: l.quantity, received: l.receivedQuantity })));
      setShowReceiveForm(true);
    } catch { toast.error('Erro'); }
  };
  const doReceive = async () => {
    try {
      await api.post(`/purchases/orders/${receivingOrder.id}/receive`, { warehouseId: receivingOrder.warehouseId, lines: receiveLines.filter(l => l.quantity > 0).map(l => ({ lineId: l.lineId, quantity: parseFloat(l.quantity) })) });
      toast.success('Mercadoria recebida!'); setShowReceiveForm(false); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
  };
  const viewOrder = async (id) => { try { const res = await api.get(`/purchases/orders/${id}`); setSelectedOrder(res.data); } catch { toast.error('Erro'); } };

  // ============================================
  // PURCHASE INVOICES (Faturas de Fornecedores)
  // ============================================
  const openPurchaseTrackingModal = ({ lineName, quantity, lotEnabled, serialEnabled, initialLotNumber, initialExpiryDate, initialSerials }) => {
    return new Promise((resolve) => {
      const serialQty = serialEnabled ? Math.max(0, Math.trunc(Number(quantity || 0))) : 0;
      purchaseTrackingResolverRef.current = resolve;
      setPurchaseTrackingModal({
        open: true,
        lineName,
        quantity,
        lotEnabled,
        serialEnabled,
        lotNumber: initialLotNumber || '',
        expiryDate: initialExpiryDate || '',
        serialValues: Array.from({ length: serialQty }, (_, idx) =>
          Array.isArray(initialSerials) ? (initialSerials[idx] || '') : ''
        )
      });
    });
  };

  const closePurchaseTrackingModal = (result) => {
    if (purchaseTrackingResolverRef.current) {
      purchaseTrackingResolverRef.current(result);
      purchaseTrackingResolverRef.current = null;
    }
    setPurchaseTrackingModal(prev => ({ ...prev, open: false }));
  };

  const updatePurchaseSerialValue = (index, value) => {
    setPurchaseTrackingModal(prev => ({
      ...prev,
      serialValues: prev.serialValues.map((v, i) => (i === index ? value : v))
    }));
  };

  const handlePurchaseSerialEnter = (index, event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (event.shiftKey) {
      const prevInput = purchaseSerialInputRefs.current[index - 1];
      if (prevInput && typeof prevInput.focus === 'function') {
        prevInput.focus();
      }
      return;
    }

    const nextInput = purchaseSerialInputRefs.current[index + 1];
    if (nextInput && typeof nextInput.focus === 'function') {
      nextInput.focus();
      return;
    }
    confirmPurchaseTrackingModal();
  };

  useEffect(() => {
    if (!purchaseTrackingModal.open || !purchaseTrackingModal.serialEnabled) return;
    const timer = setTimeout(() => {
      const firstInput = purchaseSerialInputRefs.current[0];
      if (firstInput && typeof firstInput.focus === 'function') {
        firstInput.focus();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [purchaseTrackingModal.open, purchaseTrackingModal.serialEnabled, purchaseTrackingModal.serialValues.length]);

  const confirmPurchaseTrackingModal = () => {
    if (purchaseTrackingModal.lotEnabled) {
      if (!purchaseTrackingModal.lotNumber || !purchaseTrackingModal.lotNumber.trim()) {
        toast.error('Número de lote é obrigatório');
        return;
      }
    }

    const serials = (purchaseTrackingModal.serialValues || [])
      .map(s => s.trim())
      .filter(Boolean);

    if (purchaseTrackingModal.serialEnabled) {
      const qty = Number(purchaseTrackingModal.quantity || 0);
      if (!Number.isInteger(qty) || qty <= 0) {
        toast.error('Quantidade inválida para artigo com número de série');
        return;
      }
      if (serials.length !== qty) {
        toast.error(`Preencha exatamente ${qty} número(s) de série`);
        return;
      }
      if (new Set(serials.map(s => s.toUpperCase())).size !== serials.length) {
        toast.error('Existem números de série duplicados');
        return;
      }
    }

    closePurchaseTrackingModal({
      cancelled: false,
      lotNumber: purchaseTrackingModal.lotEnabled ? purchaseTrackingModal.lotNumber.trim() : '',
      expiryDate: purchaseTrackingModal.lotEnabled ? (purchaseTrackingModal.expiryDate || '') : '',
      serialNumbers: purchaseTrackingModal.serialEnabled ? serials : []
    });
  };

  const savePurchase = async () => {
    try {
      if (!purchaseForm.supplierId) { toast.error('Selecione um Fornecedor'); return; }
      const selectedDocType = purchaseForm.documentType || 'Fatura Compra';
      const isSupplierOrderDoc = selectedDocType === 'Encomenda Fornecedor' || selectedDocType === 'Orçamento Fornecedor';
      
      // Remove linhas vazias (sem articleId)
      const validLines = purchaseLines.filter(l => l.articleId && l.articleId !== '');
      
      if (validLines.length === 0) { toast.error('Adicione pelo menos 1 linha'); return; }
      
      // Ask lot/serial info when required by article configuration
      const enrichedLines = [];
      for (const line of validLines) {
        const article = articles.find(a => String(a.id) === String(line.articleId));
        const nextLine = { ...line };

        const lotEnabled = !!article?.lotControl;
        const serialEnabled = !!article?.serialNumberControl;

        if (serialEnabled) {
          const qty = Number(nextLine.quantity || 0);
          if (!Number.isInteger(qty) || qty <= 0) {
            toast.error(`O artigo ${article.reference || article.description} com número de série exige quantidade inteira`);
            return;
          }
        }

        if (lotEnabled || serialEnabled) {
          const trackingInput = await openPurchaseTrackingModal({
            lineName: article?.reference || article?.description || nextLine.description || `Artigo ${nextLine.articleId}`,
            quantity: Number(nextLine.quantity || 0),
            lotEnabled,
            serialEnabled,
            initialLotNumber: nextLine.lotNumber || '',
            initialExpiryDate: nextLine.expiryDate || '',
            initialSerials: Array.isArray(nextLine.serialNumbers) ? nextLine.serialNumbers : []
          });

          if (!trackingInput || trackingInput.cancelled) {
            return;
          }

          nextLine.lotNumber = trackingInput.lotNumber || '';
          nextLine.expiryDate = trackingInput.expiryDate || '';
          nextLine.serialNumbers = Array.isArray(trackingInput.serialNumbers) ? trackingInput.serialNumbers : [];
        }

        enrichedLines.push(nextLine);
      }

      // Se data vencimento não estiver preenchida, assume data da fatura
      const invoiceDate = purchaseForm.invoiceDate || new Date().toISOString().slice(0, 10);
      const dueDate = purchaseForm.dueDate || invoiceDate;
      
      // Prepare payload with correct types
      const payload = {
        invoiceNumber: purchaseForm.invoiceNumber || '',
        supplierId: parseInt(purchaseForm.supplierId),
        warehouseId: purchaseForm.warehouseId ? parseInt(purchaseForm.warehouseId) : null,
        invoiceDate: invoiceDate,
        dueDate: dueDate,
        supplierReference: purchaseForm.supplierReference || '',
        nãotes: purchaseForm.nãotes || '',
        paymentTerms: purchaseForm.paymentTerms || '',
        paymentMethod: purchaseForm.paymentMethod || '',
        autoUpdateStock: purchaseForm.autoUpdateStock || false,
        globalDiscountPercent: parseFloat(purchaseForm.globalDiscountPercent || 0),
        lines: enrichedLines.map(l => ({ 
          articleId: parseInt(l.articleId), 
          description: l.description || '', 
          quantity: parseFloat(l.quantity), 
          unitPrice: parseFloat(l.unitPrice), 
          discountPercent: parseFloat(l.discountPercent || 0), 
          vatPercent: parseFloat(l.vatPercent || 0), 
          vatRateId: l.vatRateId ? parseInt(l.vatRateId) : null,
          lotNumber: l.lotNumber || null,
          expiryDate: l.expiryDate || null,
          manufactureDate: l.manufactureDate || null,
          serialNumbers: Array.isArray(l.serialNumbers) ? l.serialNumbers : []
        }))
      };
      
      if (isSupplierOrderDoc) {
        const orderPayload = {
          sourceType: selectedDocType === 'Orçamento Fornecedor' ? 'quote' : 'order',
          supplierId: parseInt(purchaseForm.supplierId),
          warehouseId: purchaseForm.warehouseId ? parseInt(purchaseForm.warehouseId) : null,
          orderDate: invoiceDate,
          expectedDeliveryDate: dueDate || null,
          supplierReference: purchaseForm.supplierReference || '',
          notes: purchaseForm.nãotes || '',
          paymentTerms: purchaseForm.paymentTerms || '',
          globalDiscountPercent: parseFloat(purchaseForm.globalDiscountPercent || 0),
          lines: enrichedLines.map(l => ({
            articleId: parseInt(l.articleId),
            description: l.description || '',
            quantity: parseFloat(l.quantity),
            unitPrice: parseFloat(l.unitPrice),
            discountPercent: parseFloat(l.discountPercent || 0),
            vatPercent: parseFloat(l.vatPercent || 0),
            vatRateId: l.vatRateId ? parseInt(l.vatRateId) : null
          }))
        };

        await api.post('/purchases/orders', orderPayload);
        toast.success(selectedDocType === 'Orçamento Fornecedor' ? 'Orçamento fornecedor criado' : 'Encomenda fornecedor criada');
      } else {
        if (editingPurchase) { await api.put(`/billing/purchases/${editingPurchase.id}`, payload); toast.success('Compra atualizada'); }
        else { await api.post('/billing/purchases', payload); toast.success('Compra criada'); }
      }

      setShowPurchaseForm(false); setEditingPurchase(null); setPurchaseForm(emptyPurchase()); setPurchaseLines([]); loadAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro ao guardar compra'); }
  };
  const editPurchaseFn = (p) => { setEditingPurchase(p); setPurchaseForm({ documentType: 'Fatura Compra', invoiceNumber: p.invoiceNumber, supplierId: p.supplierId || '', warehouseId: p.warehouseId || '', invoiceDate: p.invoiceDate?.slice(0,10) || '', dueDate: p.dueDate?.slice(0,10) || '', supplierReference: p.supplierReference || '', nãotes: p.nãotes || '', paymentTerms: p.paymentTerms || '', paymentMethod: p.paymentMethod || '', autoUpdateStock: p.autoUpdateStock || false, globalDiscountPercent: p.globalDiscountPercent || 0 }); setPurchaseLines((p.lines || []).map(l => ({ id: l.id, articleId: l.articleId, description: l.description || '', quantity: l.quantity, unitPrice: l.unitPrice, discountPercent: l.discountPercent, vatPercent: l.vatPercent, vatRateId: l.vatRateId, lotNumber: l.lotNumber || '', expiryDate: l.expiryDate || '', manufactureDate: l.manufactureDate || '', serialNumbers: l.serialNumbers || [] }))); setShowPurchaseForm(true); };
  const deletePurchase = async (id) => { if (!window.confirm('Eliminar compra?')) return; try { await api.delete(`/billing/purchases/${id}`); toast.success('Compra eliminada'); loadAll(); } catch (e) { toast.error(e.response?.data?.message || 'Erro'); } };
  const postPurchase = async (id) => { if (!window.confirm('Lançar compra? Isto irá atualizar o stock se ativo.')) return; try { await api.post(`/billing/purchases/${id}/post`); toast.success('Compra lançada!'); loadAll(); } catch (e) { toast.error(e.response?.data?.message || 'Erro'); } };
  const payPurchase = async (id) => { if (!window.confirm('Marcar como paga?')) return; try { await api.post(`/billing/purchases/${id}/pay`, { paymentDate: new Date().toISOString() }); toast.success('Marcada como paga'); loadAll(); } catch (e) { toast.error(e.response?.data?.message || 'Erro'); } };
  const addPurchaseLine = () => { if (!purchaseLines.some(l => !l.articleId && !l.reference)) setPurchaseLines(prev => [...prev, { reference: '', articleId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, vatPercent: 23, vatRateId: '', lotNumber: '', expiryDate: '', manufactureDate: '', serialNumbers: [] }]); };
  const removePurchaseLine = (idx) => { setPurchaseLines(prev => prev.filter((_, i) => i !== idx)); };
  
  // Search article by reference with Enter key or search button
  const searchArticleByReference = (idx, reference) => {
    if (!reference.trim()) return;
    const art = articles.find(a => a.reference?.toLowerCase() === reference.toLowerCase());
    if (art) {
      // Article found - populate line
      setPurchaseLines(prev => {
        const upd = [...prev];
        upd[idx].articleId = art.id;
        upd[idx].reference = art.reference;
        upd[idx].description = art.name || art.description || '';
        upd[idx].unitPrice = art.purchasePriceNoVat || 0;
        upd[idx].vatPercent = art.vatRate?.percentage || 23;
        upd[idx].vatRateId = art.vatRateId;
        return upd;
      });
      toast.success(`Artigo ${art.reference} - ${art.name} encontrado`);
    } else {
      // Article nãot found - open search modal
      toast.warning('Código não encontrado. Abrindo pesquisa...');
      setSearchingLineIndex(idx);
      setArticleSearchTerm(reference);
      setShowArticleSearch(true);
    }
  };

  // Select article from search modal
  const selectArticleFromSearch = (article) => {
    // For purchase lines (when searchingLineIndex is set)
    if (searchingLineIndex !== null) {
      setPurchaseLines(prev => {
        const upd = [...prev];
        upd[searchingLineIndex].articleId = article.id;
        upd[searchingLineIndex].reference = article.reference;
        upd[searchingLineIndex].description = article.name || article.description || '';
        upd[searchingLineIndex].unitPrice = article.purchasePriceNoVat || 0;
        upd[searchingLineIndex].vatPercent = article.vatRate?.percentage || 23;
        upd[searchingLineIndex].vatRateId = article.vatRateId;
        return upd;
      });
    } 
    // For stock movements (Entry/Exit forms - adjustForm)
    else if (warehouseSubTab === 'entry' || warehouseSubTab === 'exit') {
      setAdjustForm(f => ({ ...f, articleId: article.id, reference: article.reference }));
      toast.success(`Artigo selecionado: ${article.name}`);
    }
    // For stock transfer form
    else if (warehouseSubTab === 'transfer') {
      setTransferForm(f => ({ ...f, articleId: article.id, reference: article.reference }));
      toast.success(`Artigo selecionado: ${article.name}`);
    }
    setShowArticleSearch(false);
    setSearchingLineIndex(null);
    setArticleSearchTerm('');
  };

  const updatePurchaseLine = (idx, field, value) => { 
    setPurchaseLines(prev => { 
      const upd = [...prev]; 
      upd[idx][field] = value; 
      if (field === 'articleId' && value) { 
        const art = articles.find(a => a.id === value); 
        if (art) { 
          upd[idx].reference = art.reference;
          upd[idx].description = art.name || art.description || ''; 
          upd[idx].unitPrice = art.purchasePriceNoVat || 0; 
          upd[idx].vatPercent = art.vatRate?.percentage || 23; 
          upd[idx].vatRateId = art.vatRateId; 
        } 
      } 
      return upd; 
    }); 
  };

  // Status helpers
  const statusColor = (s) => ({ Draft: '#95a5a6', Pending: '#f39c12', PartiallyReceived: '#3498db', Received: '#27ae60', Cancelled: '#e74c3c' }[s] || '#666');
  const statusLabel = (s) => ({ Draft: 'Rascunho', Pending: 'Pendente', PartiallyReceived: 'Parcial', Received: 'Recebida', Cancelled: 'Cancelada' }[s] || s);
  const movTypeLabel = (t) => ({ Entry: 'Entrada', Exit: 'Saída', Transfer: 'Transferência', Adjustment: 'Ajuste', Return: 'Devolução' }[t] || t);
  const movTypeColor = (t) => ({ Entry: '#27ae60', Exit: '#e74c3c', Transfer: '#3498db', Adjustment: '#f39c12', Return: '#9b59b6' }[t] || '#666');
  // ============================================
  // DASHBOARD MODULES
  // ============================================
  const moduleCards = [
    { key: 'invoices', icon: '📄', title: 'Faturas', subtitle: 'Consulta e gestão documental', action: () => setActiveTab('invoices') },
    { key: 'articles', icon: '📦', title: 'Artigos', subtitle: 'Produtos e serviços', action: () => navigate('/admin/billing/artigos') },
    { key: 'vatRates', icon: '💰', title: 'Taxas IVA', subtitle: 'Regras fiscais e zonas', action: () => navigate('/admin/billing/iva') },
    { key: 'payMethods', icon: '💳', title: 'Métodos', subtitle: 'Meios de pagamento', action: () => navigate('/admin/billing/metodos') },
    { key: 'receipts', icon: '🧾', title: 'Recibos', subtitle: 'Emissão e histórico', action: () => navigate('/admin/billing/recibos') },
    { key: 'avencas', icon: '🔁', title: 'Avenças', subtitle: 'Contratos recorrentes', action: () => navigate('/admin/billing/avencas') },
    { key: 'warehouse', icon: '🏭', title: 'Armazéns', subtitle: 'Locais e inventário base', action: () => navigate('/admin/billing/armazens') },
    { key: 'purchases', icon: '🛒', title: 'Compras', subtitle: 'Fornecedores e encomendas', action: () => navigate('/admin/purchases') },
    { key: 'sales', icon: '💼', title: 'Vendas', subtitle: 'Operação comercial', action: () => navigate('/admin/sales') },
    { key: 'stocks', icon: '📊', title: 'Stocks', subtitle: 'Movimentos e ajustes', action: () => navigate('/admin/billing/stocks') },
    { key: 'familias', icon: '🧩', title: 'Famílias', subtitle: 'Categorias de artigos', action: () => navigate('/admin/billing/familias') },
  ];

  const centralCards = [
    { key: 'analytics', icon: '📈', title: 'Relatórios', subtitle: 'Dashboards e KPIs', action: () => navigate('/admin/analytics') },
    { key: 'analyticsPro', icon: '🧩', title: 'Relatórios PRO', subtitle: 'Agrupar / filtros / exportação', action: () => navigate('/admin/analytics-pro') },
  ];

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <DashboardLayout userType="admin">
      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: 'var(--info-text)' }}>💼 Faturação & ERP</h1>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--info)', fontSize: '0.92rem' }}>Gestão de faturação, compras, vendas e operações de armazém</p>
          </div>
          {activeTab !== 'overview' && (
            <button
              onClick={() => setActiveTab('overview')}
              style={{
                border: 'none',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#fff',
                borderRadius: '10px',
                padding: '0.6rem 1rem',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '0.85rem',
                boxShadow: '0 8px 20px rgba(37, 99, 235, 0.25)'
              }}
            >
              ← Voltar ao Dashboard
            </button>
          )}
        </div>

        {/* ============================================ */}
        {/* TAB: DASHBOARD */}
        {/* ============================================ */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {[
                { l: 'Faturas', v: invoices.length, c: 'var(--info)', i: '\uD83D\uDCC4' },
                { l: 'Artigos', v: articles.length, c: 'var(--success)', i: '\uD83D\uDCE6' },
                { l: 'Recibos', v: receipts.length, c: 'var(--accent-secondary)', i: '\uD83E\uDDFE' },
                { l: 'Pendentes', v: invoices.filter(f => f.status === 'Pending').length, c: 'var(--warning)', i: '\u23F3' },
                { l: 'Armaz\u00E9ns', v: warehouseStats.totalWarehouses || 0, c: 'var(--info-text)', i: '\uD83C\uDFE2' },
                { l: 'Fornecedores', v: purchaseStats.totalSuppliers || 0, c: 'var(--warning)', i: '\uD83E\uDD1D' },
                { l: 'Stock Baixo', v: warehouseStats.lowStockCount || 0, c: 'var(--error)', i: '\u26A0\uFE0F' },
                { l: 'Valor Stock', v: fmtMoney(warehouseStats.totalStockValue || 0), c: 'var(--success-text)', i: '\uD83D\uDCB0' },
              ].map((s, i) => (
                <div key={i} style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '2rem' }}>{s.i}</span>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{s.l}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: s.c }}>{s.v}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem', gap: '0.75rem', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--info-text)', fontWeight: '800' }}>Módulos de Faturação e ERP</h2>
                <span style={{ fontSize: '0.78rem', color: 'var(--info)', fontWeight: 600 }}>Estrutura rápida, funcional e profissional</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.85rem' }}>
                {moduleCards.map((module) => (
                  <button
                    key={module.key}
                    onClick={module.action}
                    style={{
                      border: '1px solid var(--info)',
                      borderRadius: '14px',
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                      padding: '0.9rem 0.95rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.7rem',
                      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 24px rgba(37, 99, 235, 0.14)';
                      e.currentTarget.style.borderColor = '#93c5fd';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(15, 23, 42, 0.06)';
                      e.currentTarget.style.borderColor = '#dbeafe';
                    }}
                  >
                    <span style={{ fontSize: '1.35rem', lineHeight: 1 }}>{module.icon}</span>
                    <span>
                      <div style={{ fontWeight: 800, color: 'var(--info-text)', fontSize: '0.9rem' }}>{module.title}</div>
                      <div style={{ color: 'var(--info)', fontSize: '0.78rem', marginTop: '0.2rem' }}>{module.subtitle}</div>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem', gap: '0.75rem', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--info-text)', fontWeight: '800' }}>Central</h2>
                <span style={{ fontSize: '0.78rem', color: 'var(--info)', fontWeight: 600 }}>Relatórios e Marketing ficam concentrados aqui</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.85rem' }}>
                {centralCards.map((module) => (
                  <button
                    key={module.key}
                    onClick={module.action}
                    style={{
                      border: '1px solid var(--border-color)',
                      borderRadius: '14px',
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                      padding: '0.9rem 0.95rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.7rem',
                      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 24px rgba(15, 23, 42, 0.10)';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(15, 23, 42, 0.06)';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <span style={{ fontSize: '1.35rem', lineHeight: 1 }}>{module.icon}</span>
                    <span>
                      <div style={{ fontWeight: 800, color: 'var(--info-text)', fontSize: '0.9rem' }}>{module.title}</div>
                      <div style={{ color: 'var(--info)', fontSize: '0.78rem', marginTop: '0.2rem' }}>{module.subtitle}</div>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: FATURAS */}
        {/* ============================================ */}
        {activeTab === 'invoices' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle} className="adm-table">
              <thead><tr style={thRow}>
                <th style={th}>Nº</th><th style={th}>Tipo</th><th style={th}>Cliente</th>
                <th style={th}>Data</th><th style={th}>Venc.</th><th style={th}>Total</th>
                <th style={th}>Status</th><th style={th}>Ações</th>
              </tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} style={trStyle}>
                    <td style={td}>
                      <strong>{inv.invoiceNumber}</strong>
                      <button
                        onClick={() => toast.info('Pesquisa de fatura: ' + inv.invoiceNumber)}
                        style={{ marginLeft: '6px', background: 'var(--info)', color: '#fff', border: 'none', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                        title="Pesquisar fatura"
                      >🔍</button>
                    </td>
                    <td style={td} data-label="Tipo">{inv.documentType}</td>
                    <td style={td} data-label="Cliente">{inv.clientName}</td>
                    <td style={td} data-label="Data">{fmtDate(inv.issueDate)}</td>
                    <td style={td} data-label="Venc.">{fmtDate(inv.dueDate)}</td>
                    <td style={td} data-label="Total"><strong>{fmtMoney(inv.totalAmount)}</strong></td>
                    <td style={td} data-label="Status"><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: inv.status === 'Paid' ? '#d5f5e3' : inv.status === 'Cancelled' ? '#fadbd8' : '#fef9e7', color: inv.status === 'Paid' ? '#27ae60' : inv.status === 'Cancelled' ? '#e74c3c' : '#f39c12' }}>{inv.status === 'Paid' ? 'Paga' : inv.status === 'Cancelled' ? 'Anulada' : 'Pendente'}</span></td>
                    <td style={td} className="adm-actions">
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => { setPreviewDoc(inv); setShowDocPreview(true); }} style={btnSm} title="Pré-visualizar">👁️</button>
                        <button onClick={() => printInvoiceAction(inv)} style={btnSm} title="PDF">📄</button>
                        <button onClick={() => emailInvoiceAction(inv)} style={btnSm} title="Email">✉️</button>
                        {inv.status === 'Pending' && <button onClick={() => { setPayingInvoice(inv); setShowPayModal(true); }} style={{ ...btnSm, background: 'var(--success)' }} title="Pagar">💳</button>}
                        {inv.status === 'Pending' && <button onClick={() => cancelInvoice(inv.id)} style={{ ...btnSm, background: 'var(--error)' }} title="Anular">✖️</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* TAB: NOVA FATURA foi removida daqui (existe em Vendas). */}
        {/* ============================================ */}
        {/* TAB: ARTIGOS */}
        {/* ============================================ */}
        {activeTab === 'articles' && (
          <div>
            {!showArticleForm ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input style={{ ...inp, maxWidth: '300px' }} placeholder={' Pesquisar artigos...'} value={searchArticle} onChange={e => setSearchArticle(e.target.value)} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={recalculateArticleStock} style={{ background: 'var(--accent-secondary)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>Recalcular Stock</button>
                    <button onClick={openNewArticle} style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}> Novo Artigo</button>
                  </div>
                </div>
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--info)', borderRadius: '12px', overflow: 'auto', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead><tr style={{ background: 'var(--info-bg)' }}>
                      <th style={th}>Img</th><th style={th}>Referência</th><th style={th}>Nome</th><th style={th}>Marca</th><th style={th}>Família</th><th style={th}>P.Venda</th><th style={th}>IVA</th><th style={th}>Stock</th><th style={th}>Stock Vendável</th><th style={th}>Lote</th><th style={th}>Série</th><th style={th}>Ações</th>
                    </tr></thead>
                    <tbody>
                      {articles.filter(a => !searchArticle || a.description?.toLowerCase().includes(searchArticle.toLowerCase()) || a.reference?.toLowerCase().includes(searchArticle.toLowerCase()) || a.brand?.toLowerCase().includes(searchArticle.toLowerCase())).map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}>{a.imageUrl ? <img src={a.imageUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} /> : <div style={{ width: 32, height: 32, background: 'var(--bg-tertiary)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>🖼️</div>}</td>
                          <td style={td}><strong>{a.reference}</strong></td>
                          <td style={td}>{a.description}</td>
                          <td style={td}>{a.brand || '-'}</td>
                          <td style={td}><span style={{ background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{a.familyId || '-'}</span></td>
                          <td style={td}>{fmtMoney(a.salePriceWithVat)}</td>
                          <td style={td}>{a.vatRateId ? vatRates.find(v => v.id === a.vatRateId)?.percentage || '-' : '-'}%</td>
                          <td style={td}>{(a.controlsStock || a.stockControl) ? <span style={{ color: a.stock <= 0 ? '#e74c3c' : '#27ae60', fontWeight: '600' }}>{a.stock}</span> : <span style={{ color: 'var(--text-muted)' }}>N/A</span>}</td>
                          <td style={td}>{(a.controlsStock || a.stockControl) ? <span style={{ color: Number(a.sellableStock ?? (a.stock - (a.customerOrderQty || 0))) <= 0 ? '#e74c3c' : '#2980b9', fontWeight: '700' }}>{Number(a.sellableStock ?? (a.stock - (a.customerOrderQty || 0))).toFixed(4).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')}</span> : <span style={{ color: 'var(--text-muted)' }}>N/A</span>}</td>
                          <td style={td}>{a.lotControl ? <span style={{ background: 'var(--success-bg)', color: 'var(--success-text)', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{a.lotStrategy}</span> : '-'}</td>
                          <td style={td}>{a.serialNumberControl ? <span style={{ background: 'var(--info-bg)', color: 'var(--accent-primary)', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>Ativo</span> : '-'}</td>
                          <td style={td}>
                            <button onClick={() => openEditArticle(a)} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', marginRight: '4px', fontSize: '11px' }}>Editar</button>
                            <button onClick={() => deleteArticle(a.id)} style={{ background: 'var(--error)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Remover</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {articles.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Nenhum artigo encontrado</p>}
                </div>
              </div>
            ) : (
              <div>
                {/* Article Form Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid var(--info)', borderRadius: '12px', padding: '0.75rem 1rem' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700' }}>{articleForm.id ? ' Editar Artigo' : ' Novo Artigo'}</h3>
                  <div>
                    <button onClick={saveArticle} style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', marginRight: '8px' }}> Gravar</button>
                    <button onClick={() => setShowArticleForm(false)} style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
                  </div>
                </div>
                {/* Article Sub-Tabs */}
                <div style={{ display: 'flex', gap: '2px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {[
                    {k:'dados',l:'Dados'},
                    {k:'precos',l:'Preços'},
                    {k:'stock',l:'Stock'},
                    {k:'fornecedor',l:'Fornecedor'},
                    {k:'nutricional',l:'Nutricional'},
                    {k:'lotes',l:'Lotes'},
                    ...(articleForm.serialNumberControl ? [{ k: 'serials', l: 'Número de Série / IMEI' }] : []),
                  ].map(st => (
                    <button key={st.k} onClick={() => setArticleFormTab(st.k)} style={{ padding: '8px 16px', border: 'none', borderBottom: articleFormTab === st.k ? '3px solid var(--info)' : '3px solid transparent', background: articleFormTab === st.k ? '#fff' : '#f8f9fa', color: articleFormTab === st.k ? '#3498db' : '#555', cursor: 'pointer', fontWeight: articleFormTab === st.k ? '700' : '400', fontSize: '13px' }}>{st.l}</button>
                  ))}
                </div>
                {/* Image + Reference Row */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--info)', borderRadius: '12px', padding: '16px', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)' }}>
                  {/* SUB-TAB: DADOS */}
                  {articleFormTab === 'dados' && (
                    <div>
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        {/* Image */}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ width: '120px', height: '120px', border: '2px dashed var(--border-color)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '6px' }}>
                            {articleForm.imageUrl ? <img src={articleForm.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: 'var(--text-muted)', fontSize: '40px' }}></span>}
                          </div>
                          <label style={{ background: 'var(--info)', color: '#fff', padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>
                             Imagem
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { setArticleImageFile(e.target.files[0]); if(e.target.files[0]) setArticleForm(f => ({ ...f, imageUrl: URL.createObjectURL(e.target.files[0]) })); }} />
                          </label>
                          <button onClick={searchImageByAI} disabled={aiImageLoading} style={{ marginTop: '6px', background: 'var(--accent-secondary)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '6px', cursor: aiImageLoading ? 'not-allowed' : 'pointer', fontSize: '11px', opacity: aiImageLoading ? 0.7 : 1 }}>
                            {aiImageLoading ? 'A procurar...' : '🧠 Procurar imagem IA'}
                          </button>
                        </div>
                        {/* Main fields */}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                            <div><label style={lbl}>REFERÊNCIA *</label><input style={inp} value={articleForm.reference} onChange={e => setArticleForm(f => ({ ...f, reference: e.target.value }))} /></div>
                            <div><label style={lbl}>NOME / DESC. CURTA</label><input style={inp} value={articleForm.shortDescription || ''} onChange={e => setArticleForm(f => ({ ...f, shortDescription: e.target.value }))} placeholder="Nome comercial do produto" /></div>
                            <div><label style={lbl}>FAMÍLIA</label><select style={inp} value={articleForm.familyId || ''} onChange={e => setArticleForm(f => ({ ...f, familyId: e.target.value ? parseInt(e.target.value) : null }))}><option value="">Sem família</option>{families.map(fm => <option key={fm.id} value={fm.id}>{fm.name}</option>)}</select></div>
                            <div><label style={lbl}>MARCA</label><input style={inp} value={articleForm.brand || ''} onChange={e => setArticleForm(f => ({ ...f, brand: e.target.value }))} placeholder="Ex: Prozis, Optimum Nutrition..." /></div>
                            <div><label style={lbl}>CÓDIGO BARRAS</label><input style={inp} value={articleForm.barcode || ''} onChange={e => setArticleForm(f => ({ ...f, barcode: e.target.value }))} /></div>
                            <div><label style={lbl}>UNIDADE</label><select style={inp} value={articleForm.unit || 'UN'} onChange={e => setArticleForm(f => ({ ...f, unit: e.target.value }))}><option>UN</option><option>KG</option><option>LT</option><option>MT</option><option>CX</option><option>PAR</option></select></div>
                            <div><label style={lbl}>TIPO</label><select style={inp} value={articleForm.articleType || 'Produto'} onChange={e => setArticleForm(f => ({ ...f, articleType: e.target.value }))}><option value="Produto">Produto</option><option value="Serviço">Serviço</option></select></div>
                            <div><label style={lbl}>CATEGORIA</label><input style={inp} value={articleForm.category || ''} onChange={e => setArticleForm(f => ({ ...f, category: e.target.value }))} placeholder="Ex: Alimentação, Suplementos..." /></div>
                            <div><label style={lbl}>TAXA IVA %</label><select style={inp} value={articleForm.vatRateId || ''} onChange={e => setArticleForm(f => ({ ...f, vatRateId: e.target.value ? parseInt(e.target.value) : null }))}><option value="">0%</option>{vatRates.map(v => <option key={v.id} value={v.id}>{v.percentage}% - {v.description}</option>)}</select></div>
                          </div>
                          <div style={{ marginTop: '8px' }}><label style={lbl}>DESCRIÇÃO</label><textarea style={{ ...inp, minHeight: '50px' }} value={articleForm.description || ''} onChange={e => setArticleForm(f => ({ ...f, description: e.target.value }))} /></div>
                          <div style={{ marginTop: '8px', display: 'flex', gap: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><input type="checkbox" checked={articleForm.isActive !== false} onChange={e => setArticleForm(f => ({ ...f, isActive: e.target.checked }))} /> Ativo</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><input type="checkbox" checked={articleForm.controlsStock || false} onChange={e => setArticleForm(f => ({ ...f, controlsStock: e.target.checked }))} /> Controlo Stock</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* SUB-TAB: PREÇOS */}
                  {articleFormTab === 'precos' && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}> Preços de Venda (5 Níveis)</h4>
                      <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>Preencha o preço sem IVA e pressione Enter para calcular o preço com IVA automaticamente (ou vice-versa).</p>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ background: 'var(--info-bg)' }}>
                          <th style={th}>Nível</th><th style={th}>Preço s/ IVA</th><th style={th}>Preço c/ IVA</th><th style={th}>Margem %</th>
                        </tr></thead>
                        <tbody>
                          {[1,2,3,4,5].map(n => {
                            const fNoVat = n === 1 ? 'salePriceNoVat' : `salePrice${n}NoVat`;
                            const fWithVat = n === 1 ? 'salePriceWithVat' : `salePrice${n}WithVat`;
                            return (
                            <tr key={n} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={td}><strong>Preço {n}</strong></td>
                              <td style={td}><input style={{ ...inp, width: '120px' }} type="number" step="0.01" value={articleForm[fNoVat] || ''} onChange={e => setArticleForm(f => ({ ...f, [fNoVat]: e.target.value }))} onKeyDown={e => { if(e.key==='Enter') handlePriceNoVatEnter(n, articleForm[fNoVat]); }} placeholder="0.00" /></td>
                              <td style={td}><input style={{ ...inp, width: '120px' }} type="number" step="0.01" value={articleForm[fWithVat] || ''} onChange={e => setArticleForm(f => ({ ...f, [fWithVat]: e.target.value }))} onKeyDown={e => { if(e.key==='Enter') handlePriceWithVatEnter(n, articleForm[fWithVat]); }} placeholder="0.00" /></td>
                              <td style={td}><input style={{ ...inp, width: '80px' }} type="number" step="0.01" value={articleForm[`margin${n}`] || ''} onChange={e => setArticleForm(f => ({ ...f, [`margin${n}`]: e.target.value }))} placeholder="%" /></td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', marginTop: '12px' }}>
                        <div><label style={lbl}>PREÇO CUSTO s/ IVA</label><input style={inp} type="number" step="0.01" value={articleForm.costPriceNoVat || ''} onChange={e => setArticleForm(f => ({ ...f, costPriceNoVat: e.target.value }))} /></div>
                        <div><label style={lbl}>PREÇO CUSTO c/ IVA</label><input style={inp} type="number" step="0.01" value={articleForm.costPriceWithVat || ''} onChange={e => setArticleForm(f => ({ ...f, costPriceWithVat: e.target.value }))} /></div>
                        <div><label style={lbl}>PREÇO CUSTO MÉDIO</label><input style={inp} type="number" step="0.01" value={articleForm.averageCostPrice || ''} onChange={e => setArticleForm(f => ({ ...f, averageCostPrice: e.target.value }))} /></div>
                        <div><label style={lbl}>ÚLTIMO PREÇO COMPRA</label><input style={inp} type="number" step="0.01" value={articleForm.lastPurchasePrice || ''} onChange={e => setArticleForm(f => ({ ...f, lastPurchasePrice: e.target.value }))} /></div>
                      </div>
                    </div>
                  )}

                  {/* SUB-TAB: STOCK */}
                  {articleFormTab === 'stock' && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}> Stock e Lotes</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                        <div><label style={lbl}>STOCK</label><input style={{ ...inp, background: 'var(--bg-card)' }} type="number" value={articleForm.currentStock || articleForm.stock || 0} readOnly /></div>
                        <div><label style={lbl}>QTD ENCOMENDADA CLIENTE</label><input style={{ ...inp, background: 'var(--bg-card)' }} type="number" value={articleForm.customerOrderQty || 0} readOnly /></div>
                        <div><label style={lbl}>QTD ENCOMENDA FORNECEDOR</label><input style={{ ...inp, background: 'var(--bg-card)' }} type="number" value={articleForm.supplierOrderQty || 0} readOnly /></div>
                        <div><label style={lbl}>STOCK VENDÁVEL</label><input style={{ ...inp, background: 'var(--bg-card)', fontWeight: '700', color: Number(articleForm.sellableStock || 0) <= 0 ? '#e74c3c' : '#2c3e50' }} type="number" value={articleForm.sellableStock ?? ((articleForm.currentStock || articleForm.stock || 0) - (articleForm.customerOrderQty || 0))} readOnly /></div>
                        <div><label style={lbl}>STOCK MÍNIMO</label><input style={inp} type="number" value={articleForm.minimumStock || ''} onChange={e => setArticleForm(f => ({ ...f, minimumStock: e.target.value }))} /></div>
                        <div><label style={lbl}>STOCK MÁXIMO</label><input style={inp} type="number" value={articleForm.maximumStock || ''} onChange={e => setArticleForm(f => ({ ...f, maximumStock: e.target.value }))} /></div>
                        <div><label style={lbl}>PONTO ENCOMENDA</label><input style={inp} type="number" value={articleForm.reorderPoint || ''} onChange={e => setArticleForm(f => ({ ...f, reorderPoint: e.target.value }))} /></div>
                        <div><label style={lbl}>LOCALIZAÇÃO</label><input style={inp} value={articleForm.location || ''} onChange={e => setArticleForm(f => ({ ...f, location: e.target.value }))} /></div>
                      </div>
                      <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><input type="checkbox" checked={articleForm.lotControl || false} onChange={e => setArticleForm(f => ({ ...f, lotControl: e.target.checked }))} /> Controlo por Lote</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><input type="checkbox" checked={articleForm.serialNumberControl || false} onChange={e => setArticleForm(f => ({ ...f, serialNumberControl: e.target.checked }))} /> Controlo por Número de Série</label>
                        {articleForm.lotControl && (
                          <div><label style={lbl}>ESTRATÉGIA</label><select style={inp} value={articleForm.lotStrategy || 'FIFO'} onChange={e => setArticleForm(f => ({ ...f, lotStrategy: e.target.value }))}><option value="FIFO">FIFO (First In, First Out)</option><option value="LIFO">LIFO (Last In, First Out)</option><option value="FOFI">FOFI (First Out, First In - por validade)</option><option value="FEFO">FEFO (First Expired, First Out)</option></select></div>
                        )}
                      </div>

                      {!!editingArticle?.id && (
                        <div style={{ marginTop: '14px' }}>
                          <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Stock por Armazém</h5>
                          {articleWarehouseStocks.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '12px' }}>Sem stock por armazém para este artigo.</p>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '10px', alignItems: 'start' }}>
                              <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--info-bg)' }}>
                                      <th style={th}>Id</th>
                                      <th style={th}>Descrição Armazém</th>
                                      <th style={th}>Existência</th>
                                      <th style={th}>Qtd. Enc. Cliente</th>
                                      <th style={th}>Qtd. Enc. Fornecedor</th>
                                      <th style={th}>Stock Vendável</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {articleWarehouseStocks.map(ws => (
                                      <tr
                                        key={ws.warehouseId}
                                        onClick={() => setSelectedArticleWarehouseStockId(ws.warehouseId)}
                                        style={{
                                          borderBottom: '1px solid var(--border-color)',
                                          background: selectedArticleWarehouseStockId === ws.warehouseId ? '#ecf5ff' : 'transparent',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        <td style={td}>{ws.warehouseId}</td>
                                        <td style={td}>{ws.warehouseName || '-'}</td>
                                        <td style={td}>{Number(ws.currentStock || 0).toFixed(2)}</td>
                                        <td style={td}>{Number(ws.customerOrderQty || 0).toFixed(2)}</td>
                                        <td style={td}>{Number(ws.supplierOrderQty || 0).toFixed(2)}</td>
                                        <td style={{ ...td, color: Number(ws.sellableStock || 0) <= 0 ? '#e74c3c' : '#2c3e50', fontWeight: 600 }}>{Number(ws.sellableStock || 0).toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {(() => {
                                const selectedStock = articleWarehouseStocks.find(ws => ws.warehouseId === selectedArticleWarehouseStockId) || articleWarehouseStocks[0];
                                if (!selectedStock) return null;
                                return (
                                  <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                      <div><label style={lbl}>Qtd. Mínima</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.minimumStock || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Armazém</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={selectedStock.warehouseName || '-'} readOnly /></div>
                                      <div><label style={lbl}>Qtd. Reposição</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.reorderQuantity || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Existência</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.currentStock || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Local Armazém</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={selectedStock.warehouseLocation || ''} readOnly /></div>
                                      <div><label style={lbl}>Último Preço</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={fmtMoney(selectedStock.lastPurchasePrice || 0)} readOnly /></div>
                                      <div><label style={lbl}>Qtd. Enc. Cliente</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.customerOrderQty || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Preço de Custo Médio</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={fmtMoney(selectedStock.averageCostPrice || 0)} readOnly /></div>
                                      <div><label style={lbl}>Consignação Cliente</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.customerConsignmentQty || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Última Entrada</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={selectedStock.lastEntryDate ? new Date(selectedStock.lastEntryDate).toLocaleString() : '-'} readOnly /></div>
                                      <div><label style={lbl}>Qtd. Enc. Fornecedor</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.supplierOrderQty || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Última Saída</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={selectedStock.lastExitDate ? new Date(selectedStock.lastExitDate).toLocaleString() : '-'} readOnly /></div>
                                      <div><label style={lbl}>Consignação Fornecedor</label><input style={{ ...inp, background: 'var(--bg-card)' }} value={Number(selectedStock.supplierConsignmentQty || 0).toFixed(3)} readOnly /></div>
                                      <div><label style={lbl}>Stock Vendável</label><input style={{ ...inp, background: 'var(--bg-card)', color: Number(selectedStock.sellableStock || 0) <= 0 ? '#e74c3c' : '#2c3e50', fontWeight: 700 }} value={Number(selectedStock.sellableStock || 0).toFixed(3)} readOnly /></div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* SUB-TAB: FORNECEDOR */}
                  {articleFormTab === 'fornecedor' && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}> Histórico de Preços por Fornecedor</h4>
                      {articleSupplierPrices.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>Ainda não existem compras registadas para este artigo.</p>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr style={{ background: 'var(--info-bg)' }}>
                            <th style={th}>Cód. Fornecedor</th><th style={th}>Nome Fornecedor</th><th style={th}>Cód. Artigo Forn.</th><th style={th}>Preço s/ IVA</th><th style={th}>Preço c/ IVA</th><th style={th}>IVA %</th><th style={th}>Últ. Compra</th><th style={th}>Qtd. Últ.</th><th style={th}>Documento</th>
                          </tr></thead>
                          <tbody>
                            {articleSupplierPrices.map(sp => (
                              <tr key={sp.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={td}>{sp.supplierCode || sp.supplier?.code || sp.supplierId}</td>
                                <td style={td}><strong>{sp.supplierName || sp.supplier?.name || '-'}</strong></td>
                                <td style={td}>{sp.supplierArticleCode || '-'}</td>
                                <td style={td}>{fmtMoney(sp.lastPriceNoVat)}</td>
                                <td style={td}>{fmtMoney(sp.lastPriceWithVat)}</td>
                                <td style={td}>{sp.vatPercent ? `${sp.vatPercent}%` : '-'}</td>
                                <td style={td}>{sp.lastPurchaseDate ? new Date(sp.lastPurchaseDate).toLocaleDateString() : '-'}</td>
                                <td style={td}>{sp.lastQuantity || '-'}</td>
                                <td style={td}>{sp.purchaseOrderNumber || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                  {/* SUB-TAB: NUTRICIONAL */}
                  {articleFormTab === 'nutricional' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: 0 }}> Informação Nutricional</h4>
                        <button onClick={suggestNutritionByAI} disabled={aiNutritionLoading} style={{ background: 'var(--accent-secondary)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '7px', cursor: aiNutritionLoading ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: aiNutritionLoading ? 0.7 : 1 }}>
                          {aiNutritionLoading ? 'A analisar...' : '🧠 Procurar por IA'}
                        </button>
                      </div>
                      {!(editingArticle?.id || articleForm?.id) ? <p style={{ color: 'var(--text-muted)' }}>Grave o artigo primeiro para editar a informação nutricional.</p> : (
                        (() => {
                          const nut = articleNutrition || getEmptyNutritionInfo();
                          const nutrientRows = [
                            { k: 'energyKcal', k100: 'energyKcal100g', l: 'Energia', u: 'kcal' },
                            { k: 'fat', k100: 'fat100g', l: 'Lípidos', u: 'g' },
                            { k: 'saturatedFat', k100: 'saturatedFat100g', l: 'Saturados', u: 'g' },
                            { k: 'carbohydrates', k100: 'carbohydrates100g', l: 'Hidratos de Carbono', u: 'g' },
                            { k: 'sugars', k100: 'sugars100g', l: 'Açúcares', u: 'g' },
                            { k: 'protein', k100: 'protein100g', l: 'Proteínas', u: 'g' },
                            { k: 'salt', k100: 'salt100g', l: 'Sal', u: 'g' },
                          ];

                          return (
                            <div>
                              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '8px' }}>Pré-visualização da Declaração Nutricional</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                  <div style={{ background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '10px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>{articleForm.description || articleForm.reference || 'Artigo'} - Declaração Nutricional</div>
                                    <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
                                      <div><strong>Peso líquido:</strong> {fmtNut(nut.netWeight, 1)} g</div>
                                      <div><strong>Dose:</strong> {fmtNut(nut.servingSize, 1)} {nut.servingUnit || 'g'}</div>
                                      <div><strong>Doses por embalagem:</strong> {toN(nut.servingsPerPackage)}</div>
                                      <div><strong>Descrição dose:</strong> {nut.servingDescription || '-'}</div>
                                    </div>
                                    <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '12px' }}>
                                      <thead>
                                        <tr style={{ background: 'var(--bg-tertiary)' }}>
                                          <th style={th}>Nutriente</th>
                                          <th style={th}>Por dose</th>
                                          <th style={th}>% DR</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {nutrientRows.map(r => (
                                          <tr key={r.k} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={td}>{r.l}</td>
                                            <td style={td}>{fmtNut(nut[r.k])} {r.u}</td>
                                            <td style={td}>{calcNutDr(r.k, nut[r.k])}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div style={{ display: 'grid', gap: '8px' }}>
                                    <div style={{ background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '10px' }}><strong>Ingredientes:</strong><div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', fontSize: '12px' }}>{nut.ingredients || '-'}</div></div>
                                    <div style={{ background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '10px' }}><strong>Alergénios / Vestígios:</strong><div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', fontSize: '12px' }}>{nut.allergens || nut.allergenWarnings || '-'}</div></div>
                                    <div style={{ background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '10px' }}><strong>Instruções:</strong><div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', fontSize: '12px' }}>{nut.usageInstructions || '-'}</div></div>
                                    <div style={{ background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '10px' }}><strong>Conservação:</strong><div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', fontSize: '12px' }}>{nut.storageInstructions || '-'}</div></div>
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px', marginBottom: '12px', background: 'var(--info-bg)', padding: '10px', borderRadius: '6px' }}>
                                <div><label style={lbl}>DOSE (g/ml)</label><input style={inp} type="number" step="0.1" value={nut.servingSize || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), servingSize: e.target.value }))} /></div>
                                <div><label style={lbl}>UNIDADE</label><select style={inp} value={nut.servingUnit || 'g'} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), servingUnit: e.target.value }))}><option>g</option><option>ml</option></select></div>
                                <div><label style={lbl}>DOSES / EMBALAGEM</label><input style={inp} type="number" value={nut.servingsPerPackage || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), servingsPerPackage: e.target.value }))} /></div>
                                <div><label style={lbl}>DESCRIÇÃO DOSE</label><input style={inp} value={nut.servingDescription || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), servingDescription: e.target.value }))} placeholder="ex: 3 doseadores (30g)" /></div>
                                <div><label style={lbl}>PESO LÍQUIDO (g)</label><input style={inp} type="number" step="0.1" value={nut.netWeight || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), netWeight: e.target.value }))} /></div>
                              </div>

                              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                                <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Nutriente</th><th style={th}>Por Dose</th><th style={th}>Por 100g</th></tr></thead>
                                <tbody>
                                  {[
                                    {k:'energyKcal',k100:'energyKcal100g',l:'Energia (kcal)'},
                                    {k:'energyKj',k100:'energyKj100g',l:'Energia (kJ)'},
                                    {k:'fat',k100:'fat100g',l:'Lípidos'},
                                    {k:'saturatedFat',k100:'saturatedFat100g',l:'Saturados'},
                                    {k:'carbohydrates',k100:'carbohydrates100g',l:'Hidratos de Carbono'},
                                    {k:'sugars',k100:'sugars100g',l:'Açúcares'},
                                    {k:'fiber',k100:'fiber100g',l:'Fibra'},
                                    {k:'protein',k100:'protein100g',l:'Proteínas'},
                                    {k:'salt',k100:'salt100g',l:'Sal'},
                                  ].map(n => (
                                    <tr key={n.k} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                      <td style={td}>{n.l}</td>
                                      <td style={td}><input style={{ ...inp, width: '110px' }} type="number" step="0.01" value={nut[n.k] || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), [n.k]: e.target.value }))} /></td>
                                      <td style={td}><input style={{ ...inp, width: '110px' }} type="number" step="0.01" value={nut[n.k100] || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), [n.k100]: e.target.value }))} /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                                <div><label style={lbl}>INGREDIENTES</label><textarea style={{ ...inp, minHeight: '70px' }} value={nut.ingredients || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), ingredients: e.target.value }))} /></div>
                                <div><label style={lbl}>ALERGÉNIOS</label><textarea style={{ ...inp, minHeight: '70px' }} value={nut.allergens || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), allergens: e.target.value }))} /></div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div><label style={lbl}>AVISOS DE ALERGÉNIOS</label><textarea style={{ ...inp, minHeight: '54px' }} value={nut.allergenWarnings || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), allergenWarnings: e.target.value }))} /></div>
                                <div><label style={lbl}>CONDIÇÕES ARMAZENAMENTO</label><textarea style={{ ...inp, minHeight: '54px' }} value={nut.storageInstructions || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), storageInstructions: e.target.value }))} /></div>
                              </div>
                              <div style={{ marginTop: '8px' }}><label style={lbl}>INSTRUÇÕES DE UTILIZAÇÃO</label><textarea style={{ ...inp, minHeight: '54px' }} value={nut.usageInstructions || ''} onChange={e => setArticleNutrition(f => ({ ...(f || getEmptyNutritionInfo()), usageInstructions: e.target.value }))} /></div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}

                  {/* SUB-TAB: LOTES */}
                  {articleFormTab === 'lotes' && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}> Lotes Disponíveis em Stock</h4>
                      {!articleForm.lotControl && <p style={{ color: 'var(--warning)', marginBottom: '10px' }}> Ative o controlo por lote no separador "Stock" para utilizar esta funcionalidade.</p>}
                      {(articleForm.id || editingArticle?.id) && (
                        <div>
                          {/* Create lot form */}
                          <div style={{ background: 'var(--info-bg)', padding: '10px', borderRadius: '6px', marginBottom: '12px' }}>
                            <h5 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Criar Novo Lote</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                              <div><label style={lbl}>N.º LOTE *</label><input style={inp} value={lotForm.lotNumber} onChange={e => setLotForm(f => ({ ...f, lotNumber: e.target.value }))} /></div>
                              <div><label style={lbl}>VALIDADE</label><input style={inp} type="date" value={lotForm.expiryDate || ''} onChange={e => setLotForm(f => ({ ...f, expiryDate: e.target.value }))} /></div>
                              <div><label style={lbl}>DATA FABRICO</label><input style={inp} type="date" value={lotForm.manufactureDate || ''} onChange={e => setLotForm(f => ({ ...f, manufactureDate: e.target.value }))} /></div>
                              <div><label style={lbl}>QUANTIDADE</label><input style={inp} type="number" step="0.01" value={lotForm.quantity || ''} onChange={e => setLotForm(f => ({ ...f, quantity: e.target.value }))} /></div>
                              <div><label style={lbl}>ARMAZÉM</label><select style={inp} value={lotForm.warehouseId || ''} onChange={e => setLotForm(f => ({ ...f, warehouseId: e.target.value }))}><option value="">Nenhum</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                              <div><label style={lbl}>PREÇO COMPRA</label><input style={inp} type="number" step="0.01" value={lotForm.purchasePrice || ''} onChange={e => setLotForm(f => ({ ...f, purchasePrice: e.target.value }))} /></div>
                              <div><label style={lbl}>FORNECEDOR</label><select style={inp} value={lotForm.supplierId || ''} onChange={e => setLotForm(f => ({ ...f, supplierId: e.target.value }))}><option value="">Nenhum</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                              <div><label style={lbl}>NOTAS</label><input style={inp} value={lotForm.nãotes || ''} onChange={e => setLotForm(f => ({ ...f, nãotes: e.target.value }))} /></div>
                            </div>
                            <button onClick={saveLot} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Criar Lote</button>
                          </div>
                          {/* Lots table */}
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr style={{ background: 'var(--info-bg)' }}>
                              <th style={th}>N.º Lote</th><th style={th}>Validade</th>
                            </tr></thead>
                            <tbody>
                              {articleLots
                                .filter(lot => (Number(lot.quantity) - Number(lot.reservedQuantity || 0)) > 0)
                                .map(lot => (
                                <tr key={lot.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                  <td style={td}><strong>{lot.lotNumber}</strong></td>
                                  <td style={td}>{lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {articleLots.filter(lot => (Number(lot.quantity) - Number(lot.reservedQuantity || 0)) > 0).length === 0 && <p style={{ textAlign: 'center', padding: '15px', color: 'var(--text-muted)' }}>Nenhum lote disponível em stock</p>}
                        </div>
                      )}
                      {!(articleForm.id || editingArticle?.id) && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>Grave o artigo primeiro para gerir os lotes.</p>}
                    </div>
                  )}

                  {/* SUB-TAB: NÚMERO DE SÉRIE / IMEI */}
                  {articleFormTab === 'serials' && articleForm.serialNumberControl && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}> Números de Série / IMEI Disponíveis</h4>
                      {(articleForm.id || editingArticle?.id) && (
                        <div>
                          <div style={{ background: 'var(--info-bg)', padding: '10px', borderRadius: '6px', marginBottom: '12px' }}>
                            <h5 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Criar Número de Série / IMEI</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                              <div><label style={lbl}>SÉRIE / IMEI *</label><input style={inp} value={serialForm.serialNumber} onChange={e => setSerialForm(f => ({ ...f, serialNumber: e.target.value }))} /></div>
                              <div><label style={lbl}>LOTE (opcional)</label><select style={inp} value={serialForm.lotId || ''} onChange={e => setSerialForm(f => ({ ...f, lotId: e.target.value }))}><option value="">Nenhum</option>{articleLots.map(l => <option key={l.id} value={l.id}>{l.lotNumber}</option>)}</select></div>
                              <div><label style={lbl}>ARMAZÉM</label><select style={inp} value={serialForm.warehouseId || ''} onChange={e => setSerialForm(f => ({ ...f, warehouseId: e.target.value }))}><option value="">Nenhum</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                              <div><label style={lbl}>FORNECEDOR</label><select style={inp} value={serialForm.supplierId || ''} onChange={e => setSerialForm(f => ({ ...f, supplierId: e.target.value }))}><option value="">Nenhum</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                              <div><label style={lbl}>NOTAS</label><input style={inp} value={serialForm.notes || ''} onChange={e => setSerialForm(f => ({ ...f, notes: e.target.value }))} /></div>
                            </div>
                            <button onClick={saveSerial} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Criar Série / IMEI</button>
                          </div>

                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr style={{ background: 'var(--info-bg)' }}>
                              <th style={th}>Número de Série / IMEI</th><th style={th}>Lote</th><th style={th}>Estado</th>
                            </tr></thead>
                            <tbody>
                              {articleSerials
                                .filter(s => (s.status || '').toLowerCase() === 'available')
                                .map(s => (
                                  <tr key={s.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <td style={td}><strong>{s.serialNumber}</strong></td>
                                    <td style={td}>{s.lotNumber || '-'}</td>
                                    <td style={td}><span style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{s.status}</span></td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          {articleSerials.filter(s => (s.status || '').toLowerCase() === 'available').length === 0 && <p style={{ textAlign: 'center', padding: '15px', color: 'var(--text-muted)' }}>Nenhum número de série / IMEI disponível</p>}
                        </div>
                      )}
                      {!(articleForm.id || editingArticle?.id) && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>Grave o artigo primeiro para gerir números de série / IMEI.</p>}
                    </div>
                  )}

                </div>
                {/* End article form panel */}
              </div>
            )}
          </div>
        )}
        {/* ============================================ */}
        {/* TAB: TAXAS IVA */}
        {/* ============================================ */}
        {activeTab === 'vatRates' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Taxas de IVA</h3>
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{showVatForm ? ' Editar Taxa' : ' Nova Taxa'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                <div><label style={lbl}>CÓDIGO *</label><input style={inp} value={vatForm.code || ''} onChange={e => setVatForm(f => ({ ...f, code: e.target.value }))} /></div>
                <div><label style={lbl}>DESCRIÇÃO</label><input style={inp} value={vatForm.description || ''} onChange={e => setVatForm(f => ({ ...f, description: e.target.value }))} /></div>
                <div><label style={lbl}>PERCENTAGEM %</label><input style={inp} type="number" step="0.01" value={vatForm.percentage || ''} onChange={e => setVatForm(f => ({ ...f, percentage: e.target.value }))} /></div>
                <div><label style={lbl}>PAÍS</label><input style={inp} value={vatForm.country || 'PT'} onChange={e => setVatForm(f => ({ ...f, country: e.target.value }))} /></div>
                <div><label style={lbl}>ZONA FISCAL</label><select style={inp} value={vatForm.fiscalZone || 'PT'} onChange={e => setVatForm(f => ({ ...f, fiscalZone: e.target.value }))}>{FISCAL_ZONES.map(z => <option key={z} value={z}>{z}</option>)}</select></div>
                <div><label style={lbl}>TIPO</label><select style={inp} value={vatForm.type || 'Normal'} onChange={e => setVatForm(f => ({ ...f, type: e.target.value }))}><option>Normal</option><option>Reduzida</option><option>Intermédia</option><option>Isenta</option></select></div>
              </div>
              <button onClick={saveVatRate} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>{editingVatRate ? 'Atualizar' : 'Criar'}</button>
              {showVatForm && <button onClick={() => { setShowVatForm(false); setEditingVatRate(null); setVatForm({ code: '', description: '', name: '', percentage: 23, country: 'PT', fiscalZone: 'Continente Portugal', type: 'Normal', isDefault: false }); }} style={{ marginTop: '8px', marginLeft: '8px', background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>}
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Código</th><th style={th}>Descrição</th><th style={th}>%</th><th style={th}>País</th><th style={th}>Zona</th><th style={th}>Tipo</th><th style={th}>Ações</th></tr></thead>
                <tbody>
                  {vatRates.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={td}>{v.code}</td><td style={td}>{v.description}</td><td style={td}><strong>{v.percentage}%</strong></td><td style={td}>{v.country}</td><td style={td}>{v.fiscalZone}</td><td style={td}>{v.type}</td>
                      <td style={td}>
                        <button onClick={() => { setEditingVatRate(v); setVatForm(v); setShowVatForm(true); }} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', marginRight: '4px', fontSize: '11px' }}>Editar</button>
                        <button onClick={() => deleteVatRate(v.id)} style={{ background: 'var(--error)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: MEIOS PAGAMENTO */}
        {/* ============================================ */}
        {activeTab === 'payMethods' && (
          <div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{showPayForm ? ' Editar Meio Pagamento' : ' Novo Meio Pagamento'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                <div><label style={lbl}>CÓDIGO</label><input style={inp} value={payForm.code || ''} onChange={e => setPayForm(f => ({ ...f, code: e.target.value }))} /></div>
                <div><label style={lbl}>NOME</label><input style={inp} value={payForm.name || ''} onChange={e => setPayForm(f => ({ ...f, name: e.target.value }))} /></div>
              </div>
              <button onClick={savePayMethod} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>{showPayForm ? 'Atualizar' : 'Criar'}</button>
              {showPayForm && <button onClick={() => { setShowPayForm(null); setPayForm({}); }} style={{ marginTop: '8px', marginLeft: '8px', background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>}
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Código</th><th style={th}>Nome</th><th style={th}>Ações</th></tr></thead>
                <tbody>
                  {paymentMethods.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={td}>{p.code}</td><td style={td}>{p.name}</td>
                      <td style={td}>
                        <button onClick={() => { setShowPayForm(p); setPayForm(p); }} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', marginRight: '4px', fontSize: '11px' }}>Editar</button>
                        <button onClick={() => deletePayMethod(p.id)} style={{ background: 'var(--error)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: RECIBOS */}
        {/* ============================================ */}
        {activeTab === 'receipts' && (
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}> Recibos Emitidos</h3>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>N.º</th><th style={th}>Data</th><th style={th}>Cliente</th><th style={th}>Valor</th><th style={th}>Fatura</th><th style={th}>Tipo Pag.</th></tr></thead>
                <tbody>
                  {receipts.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={td}><strong>REC-{r.receiptNumber}</strong></td>
                      <td style={td}>{new Date(r.date).toLocaleDateString()}</td>
                      <td style={td}>{r.customerName}</td>
                      <td style={td}><strong>{fmtMoney(r.amount)}</strong></td>
                      <td style={td}>{r.invoiceNumber || '-'}</td>
                      <td style={td}>{r.paymentMethodName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {receipts.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Nenhum recibo emitido</p>}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: EMITIR RECIBO */}
        {/* ============================================ */}
        {activeTab === 'emitReceipt' && (
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}> Emitir Recibo</h3>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', maxWidth: '600px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div><label style={lbl}>FATURA</label><select style={inp} value={receiptForm.invoiceId || ''} onChange={e => setReceiptForm(f => ({ ...f, invoiceId: e.target.value }))}><option value="">Selecione...</option>{invoices.filter(i => i.status !== 'Cancelled').map(i => <option key={i.id} value={i.id}>{i.invoiceNumber} - {fmtMoney(i.totalAmount)}</option>)}</select></div>
                <div><label style={lbl}>VALOR {'€'}</label><input style={inp} type="number" step="0.01" value={receiptForm.amount || ''} onChange={e => setReceiptForm(f => ({ ...f, amount: e.target.value }))} /></div>
                <div><label style={lbl}>MEIO PAGAMENTO</label><select style={inp} value={receiptForm.paymentMethodId || ''} onChange={e => setReceiptForm(f => ({ ...f, paymentMethodId: e.target.value }))}><option value="">Selecione...</option>{paymentMethods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div><label style={lbl}>DATA</label><input style={inp} type="date" value={receiptForm.date || new Date().toISOString().split('T')[0]} onChange={e => setReceiptForm(f => ({ ...f, date: e.target.value }))} /></div>
              </div>
              <div style={{ marginTop: '8px' }}><label style={lbl}>NOTAS</label><textarea style={{ ...inp, minHeight: '40px' }} value={receiptForm.nãotes || ''} onChange={e => setReceiptForm(f => ({ ...f, nãotes: e.target.value }))} /></div>
              <button onClick={emitReceipt} style={{ marginTop: '12px', background: 'var(--success)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}> Emitir Recibo</button>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: AVENÇAS */}
        {/* ============================================ */}
        {activeTab === 'avencas' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Avenças / Contratos</h3>
              <button onClick={() => { setAvencaForm(getEmptyAvencaForm()); setShowAvencaForm(true); }} style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Nova Avença</button>
            </div>
            {showAvencaForm && (
              <div>
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Nova Avença</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                    <div><label style={lbl}>NOME *</label><input style={inp} value={avencaForm.name || ''} onChange={e => setAvencaForm(f => ({ ...f, name: e.target.value }))} /></div>
                    <div><label style={lbl}>DESCRIÇÃO</label><input style={inp} value={avencaForm.description || ''} onChange={e => setAvencaForm(f => ({ ...f, description: e.target.value }))} /></div>
                    <div><label style={lbl}>VALOR</label><input style={inp} type="number" step="0.01" value={avencaForm.amount || ''} onChange={e => setAvencaForm(f => ({ ...f, amount: e.target.value }))} /></div>
                    <div><label style={lbl}>PRIORIDADE</label><select style={inp} value={avencaForm.periodicity || 'Mensal'} onChange={e => setAvencaForm(f => ({ ...f, periodicity: e.target.value }))}><option value="Mensal">Mensal</option><option value="Anual">Anual</option></select></div>
                    <div><label style={lbl}>IVA</label><select style={inp} value={avencaForm.vatRateId || ''} onChange={e => setAvencaForm(f => ({ ...f, vatRateId: e.target.value }))}><option value="">Sem IVA</option>{vatRates.map(v => <option key={v.id} value={v.id}>{v.percentage}% - {v.description}</option>)}</select></div>
                  </div>
                  <button onClick={saveAvenca} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Gravar</button>
                  <button onClick={() => setShowAvencaForm(false)} style={{ marginTop: '8px', marginLeft: '8px', background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
                </div>
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Nome</th><th style={th}>Descrição</th><th style={th}>Valor</th><th style={th}>Prioridade</th><th style={th}>Estado</th></tr></thead>
                    <tbody>
                      {avencaTipos.map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}><strong>{a.name || '-'}</strong></td>
                          <td style={td}>{a.description || '-'}</td>
                          <td style={td}><strong>{fmtMoney(a.amount)}</strong></td>
                          <td style={td}>{a.periodicity === 'Yearly' ? 'Anual' : 'Mensal'}</td>
                          <td style={td}><span style={{ background: a.isActive ? '#d5f5e3' : '#fdd', color: a.isActive ? '#27ae60' : '#e74c3c', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{a.isActive ? 'Ativa' : 'Inativa'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {avencaTipos.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Nenhuma avença registada</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: ARMAZÉNS */}
        {/* ============================================ */}
        {activeTab === 'warehouse' && (
          <div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{editingWarehouse ? 'Editar Armazém' : 'Novo Armazém'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                <div><label style={lbl}>CÓDIGO</label><input style={inp} value={warehouseForm.code || ''} onChange={e => setWarehouseForm(f => ({ ...f, code: e.target.value }))} /></div>
                <div><label style={lbl}>NOME *</label><input style={inp} value={warehouseForm.name || ''} onChange={e => setWarehouseForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div><label style={lbl}>LOCALIZAÇÃO</label><input style={inp} value={warehouseForm.location || ''} onChange={e => setWarehouseForm(f => ({ ...f, location: e.target.value }))} /></div>
              </div>
              <div style={{ marginTop: '6px' }}><label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><input type="checkbox" checked={warehouseForm.isActive !== false} onChange={e => setWarehouseForm(f => ({ ...f, isActive: e.target.checked }))} /> Ativo</label></div>
              <button onClick={saveWarehouse} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>{editingWarehouse ? 'Atualizar' : 'Criar'}</button>
              {editingWarehouse && <button onClick={() => { setEditingWarehouse(null); setWarehouseForm({}); }} style={{ marginTop: '8px', marginLeft: '8px', background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>}
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Código</th><th style={th}>Nome</th><th style={th}>Localização</th><th style={th}>Estado</th><th style={th}>Ações</th></tr></thead>
                <tbody>
                  {warehouses.map(w => (
                    <tr key={w.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={td}>{w.code}</td><td style={td}><strong>{w.name}</strong></td><td style={td}>{w.location || '-'}</td>
                      <td style={td}><span style={{ background: w.isActive ? '#d5f5e3' : '#fdd', color: w.isActive ? '#27ae60' : '#e74c3c', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{w.isActive ? 'Ativo' : 'Inativo'}</span></td>
                      <td style={td}><button onClick={() => editWarehouse(w)} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Editar</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: STOCKS */}
        {/* ============================================ */}
        {activeTab === 'stocks' && (
          <div>
            <div style={{ display: 'flex', gap: '2px', marginBottom: '12px' }}>
              {[{k:'stock',l:'Stock Atual'},{k:'mov',l:'Movimentos'}].map(st => (
                <button key={st.k} onClick={() => setWarehouseSubTab(st.k)} style={{ padding: '8px 14px', border: 'none', borderBottom: warehouseSubTab === st.k ? '3px solid var(--accent-secondary)' : '3px solid transparent', background: warehouseSubTab === st.k ? '#fff' : '#f8f9fa', color: warehouseSubTab === st.k ? '#9b59b6' : '#555', cursor: 'pointer', fontWeight: warehouseSubTab === st.k ? '700' : '400', fontSize: '13px' }}>{st.l}</button>
              ))}
            </div>
            {warehouseSubTab === 'stock' && (
              <div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <select style={inp} value={selectedWarehouse || ''} onChange={e => { setSelectedWarehouse(e.target.value); if(e.target.value) loadWarehouseStock(e.target.value); }}><option value="">Todos os armazéns</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select>
                  <button onClick={() => setShowAdjustForm(true)} style={{ background: 'var(--warning)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Ajustar</button>
                  <button onClick={() => setShowTransferForm(true)} style={{ background: 'var(--accent-secondary)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Transferência</button>
                </div>
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Artigo</th><th style={th}>Referência</th><th style={th}>Armazém</th><th style={th}>Stock</th><th style={th}>Qtd Encomendada Cliente</th><th style={th}>Qtd Encomenda Fornecedor</th><th style={th}>Stock Vendável</th><th style={th}>Mínimo</th></tr></thead>
                    <tbody>
                      {warehouseStock.map((s,i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border-color)', background: (s.sellableStock ?? ((s.currentStock ?? s.quantity ?? 0) - (s.customerOrderQty ?? s.reservedQuantity ?? 0))) <= 0 ? '#fff5f5' : 'transparent' }}>
                          <td style={td}><strong>{s.articleName || s.article?.name || '-'}</strong></td>
                          <td style={td}>{s.articleReference || s.articleRef || s.article?.reference || '-'}</td>
                          <td style={td}>{s.warehouseName || s.warehouse?.name || '-'}</td>
                          <td style={td}>{s.currentStock ?? s.quantity ?? 0}</td>
                          <td style={td}>{s.customerOrderQty ?? s.reservedQuantity ?? 0}</td>
                          <td style={td}>{s.supplierOrderQty ?? 0}</td>
                          <td style={{ ...td, background: (s.sellableStock ?? ((s.currentStock ?? s.quantity ?? 0) - (s.customerOrderQty ?? s.reservedQuantity ?? 0))) <= 0 ? '#ffe3e3' : 'transparent' }}><strong style={{ color: (s.sellableStock ?? ((s.currentStock ?? s.quantity ?? 0) - (s.customerOrderQty ?? s.reservedQuantity ?? 0))) <= (s.minimumStock || 0) ? '#e74c3c' : '#27ae60' }}>{s.sellableStock ?? ((s.currentStock ?? s.quantity ?? 0) - (s.customerOrderQty ?? s.reservedQuantity ?? 0))}</strong></td>
                          <td style={td}>{s.minimumStock || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {warehouseStock.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Sem stock registado</p>}
                </div>
              </div>
            )}
            {warehouseSubTab === 'mov' && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Data</th><th style={th}>Tipo</th><th style={th}>Artigo</th><th style={th}>Armazém</th><th style={th}>Quant.</th><th style={th}>Motivo</th></tr></thead>
                  <tbody>
                    {stockMovements.map(m => (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}>{new Date(m.date || m.createdAt).toLocaleString()}</td>
                        <td style={td}><span style={{ background: movTypeColor(m.movementType), color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{movTypeLabel(m.movementType)}</span></td>
                        <td style={td}>{m.articleName || '-'}</td>
                        <td style={td}>{m.warehouseName || '-'}</td>
                        <td style={td}><strong style={{ color: m.quantity >= 0 ? '#27ae60' : '#e74c3c' }}>{m.quantity >= 0 ? '+' : ''}{m.quantity}</strong></td>
                        <td style={td}>{m.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {stockMovements.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Sem movimentos</p>}
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: FAMÍLIAS */}
        {/* ============================================ */}
        {activeTab === 'families' && (
          <div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{showFamilyForm ? 'Editar Família' : 'Nova Família'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                <div><label style={lbl}>CÓDIGO</label><input style={inp} value={familyForm.code || ''} onChange={e => setFamilyForm(f => ({ ...f, code: e.target.value }))} /></div>
                <div><label style={lbl}>NOME *</label><input style={inp} value={familyForm.name || ''} onChange={e => setFamilyForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div><label style={lbl}>DESCRIÇÃO</label><input style={inp} value={familyForm.description || ''} onChange={e => setFamilyForm(f => ({ ...f, description: e.target.value }))} /></div>
              </div>
              <button onClick={saveFamily} style={{ marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>{showFamilyForm ? 'Atualizar Família' : 'Criar Família'}</button>
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Código</th><th style={th}>Nome</th><th style={th}>Descrição</th><th style={th}>Ações</th></tr></thead>
                <tbody>
                  {families.map(f => (
                    <tr key={f.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={td}>{f.code || '-'}</td>
                      <td style={td}><strong>{f.name}</strong></td>
                      <td style={td}>{f.description || '-'}</td>
                      <td style={td}>
                        <button onClick={() => { setEditingFamily(f); setFamilyForm({ code: f.code, name: f.name, description: f.description }); setShowFamilyForm(true); }} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Editar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {families.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Nenhuma família</p>}
            </div>
          </div>
        )}
        {/* ============================================ */}
        {/* TAB: COMPRAS */}
        {/* ============================================ */}
        {activeTab === 'purchases' && (
          <div>
            {/* New CTA to go to dedicated purchases page */}
            <div style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', 
              borderRadius: '12px', 
              padding: '2rem', 
              marginBottom: '1.5rem',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
            }}>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: '700' }}>
                  \uD83D\uDED2 Gestão de Compras
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>
                  Aceda à página dedicada para lançar, consultar e gerir faturas de fornecedores
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/purchases')}
                style={{
                  padding: '1rem 2rem',
                  background: 'white',
                  color: 'var(--accent-primary)',
                  border: 'nãone',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  transition: 'transform 0.2s',
                  whiteSpace: 'nãowrap'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Abrir Compras \u2192
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '2px', marginBottom: '12px' }}>
              {[{k:'invoices',l:'Lançamento Compras'},{k:'orders',l:'Lançamento Encomendas'},{k:'stock',l:'Lançamento Stock'}].map(st => (
                <button key={st.k} onClick={() => setPurchaseSubTab(st.k)} style={{ padding: '8px 14px', border: 'none', borderBottom: purchaseSubTab === st.k ? '3px solid var(--warning)' : '3px solid transparent', background: purchaseSubTab === st.k ? '#fff' : '#f8f9fa', color: purchaseSubTab === st.k ? '#e67e22' : '#555', cursor: 'pointer', fontWeight: purchaseSubTab === st.k ? '700' : '400', fontSize: '13px' }}>{st.l}</button>
              ))}
            </div>
            <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info)', borderRadius: '8px', padding: '12px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '13px', color: 'var(--info)' }}>
                \uD83D\uDCA1 <strong>Gestão de Fornecedores:</strong> Crie e edite fornecedores clicando no botão ao lado \u2192
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => { setEditingSupplier(null); setSupplierForm(emptySupplier()); setShowSupplierForm(true); }}
                  style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}
                >
                  \u2795 Novo Fornecedor
                </button>
                <button 
                  onClick={() => { 
                    if (suppliers.length === 0) {
                      toast.warning('Nenhum fornecedor dispon�vel para editar');
                      return;
                    }
                    // Open list to select which supplier to edit
                    setSupplierSearchTerm('');
                    setShowSupplierSearch(true);
                  }}
                  style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}
                >
                  \u270F\uFE0F Editar Fornecedor
                </button>
              </div>
            </div>
            {/* Orders sub-tab (Encomendas) */}
            {purchaseSubTab === 'orders' && (
              <div>
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>➕ Nova Encomenda</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                    <div><label style={lbl}>FORNECEDOR *</label><select style={inp} value={orderForm.supplierId || ''} onChange={e => setOrderForm(f => ({ ...f, supplierId: e.target.value }))}><option value="">Selecione...</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                    <div><label style={lbl}>ARMAZÉM DESTINO *</label><select style={inp} value={orderForm.warehouseId || ''} onChange={e => setOrderForm(f => ({ ...f, warehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                    <div><label style={lbl}>DATA ESPERADA</label><input style={inp} type="date" value={orderForm.expectedDate || ''} onChange={e => setOrderForm(f => ({ ...f, expectedDate: e.target.value }))} /></div>
                  </div>
                  <div style={{ marginTop: '8px' }}><label style={lbl}>NOTAS</label><textarea style={{ ...inp, minHeight: '40px' }} value={orderForm.nãotes || ''} onChange={e => setOrderForm(f => ({ ...f, nãotes: e.target.value }))} /></div>
                  {/* Lines */}
                  <h5 style={{ fontSize: '12px', fontWeight: '600', marginTop: '10px', marginBottom: '6px' }}>Linhas da Encomenda</h5>
                  {orderLines.map((line, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '4px', alignItems: 'center' }}>
                      <select style={{ ...inp, flex: 2 }} value={line.articleId || ''} onChange={e => updateOrderLine(idx, 'articleId', e.target.value)}><option value="">Artigo...</option>{articles.map(a => <option key={a.id} value={a.id}>{a.reference} - {a.name}</option>)}</select>
                      <input style={{ ...inp, width: '80px' }} type="number" placeholder="Qtd" value={line.quantity || ''} onChange={e => updateOrderLine(idx, 'quantity', e.target.value)} />
                      <input style={{ ...inp, width: '80px' }} type="number" step="0.01" placeholder="Preço" value={line.unitPrice || ''} onChange={e => updateOrderLine(idx, 'unitPrice', e.target.value)} />
                      <button onClick={() => setOrderLines(ls => ls.filter((_,i) => i !== idx))} style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}></button>
                    </div>
                  ))}
                  <button onClick={addOrderLine} style={{ background: 'var(--bg-tertiary)', border: 'nãone', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginTop: '4px' }}>Linha</button>
                  <div style={{ marginTop: '10px' }}>
                    <button onClick={saveOrder} style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', marginRight: '8px' }}>Criar Encomenda</button>
                  </div>
                </div>
                {/* Orders list */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>N.º</th><th style={th}>Fornecedor</th><th style={th}>Armazém</th><th style={th}>Data</th><th style={th}>Itens</th><th style={th}>Total</th><th style={th}>Estado</th><th style={th}>Ações</th></tr></thead>
                    <tbody>
                      {purchaseOrders.map(o => (
                        <tr key={o.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}><strong>EC-{o.orderNumber || o.id}</strong></td>
                          <td style={td}>{o.supplierName || o.supplier?.name || '-'}</td>
                          <td style={td}>{o.warehouseName || o.warehouse?.name || '-'}</td>
                          <td style={td}>{new Date(o.orderDate || o.createdAt).toLocaleDateString()}</td>
                          <td style={td}>{o.lineCount || o.lines?.length || 0}</td>
                          <td style={td}><strong>{fmtMoney(o.totalAmount)}</strong></td>
                          <td style={td}><span style={{ background: statusColor(o.status), color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{statusLabel(o.status)}</span></td>
                          <td style={td}>
                            <button onClick={() => viewOrder(o.id)} style={{ background: 'var(--info)', color: '#fff', border: 'nãone', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginRight: '2px' }}></button>
                            {o.status === 'Confirmed' && <button onClick={() => openReceive(o)} style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginRight: '2px' }}></button>}
                            {o.status === 'Draft' && <button onClick={() => confirmOrder(o.id)} style={{ background: 'var(--warning)', color: '#fff', border: 'nãone', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginRight: '2px' }}></button>}
                            {o.status === 'Draft' && <button onClick={() => cancelOrderFn(o.id)} style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {purchaseOrders.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Nenhuma encomenda</p>}
                </div>
              </div>
            )}

            {/* Purchase Invoices sub-tab (Lançamento de Compras) */}
            {purchaseSubTab === 'invoices' && (
              <div>
                <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info)', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>📥</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--info)', marginBottom: '4px' }}>Separador de Lançamento</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>Este separador destina-se apenas ao lançamento de novas compras. Para consultar e pesquisar compras existentes, aceda ao separador <strong>📊 Relatórios</strong>.</div>
                  </div>
                </div>
                {!showPurchaseForm && (
                  <button onClick={() => { setShowPurchaseForm(true); setEditingPurchase(null); setPurchaseForm(emptyPurchase()); setPurchaseLines([{ articleId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, vatPercent: 23, vatRateId: '', lotNumber: '', expiryDate: '', manufactureDate: '', serialNumbers: [] }]); }} style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', marginBottom: '12px' }}>➕ Novo Documento Fornecedor</button>
                )}
                {showPurchaseForm && (
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>{editingPurchase ? '✏️ Editar Compra' : `📄 Novo ${purchaseForm.documentType || 'Documento Fornecedor'}`}</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                    <div><label style={lbl}>TIPO DOCUMENTO</label><select style={inp} value={purchaseForm.documentType || 'Fatura Compra'} onChange={e => setPurchaseForm(f => ({ ...f, documentType: e.target.value }))}><option>Fatura Compra</option><option>Encomenda Fornecedor</option><option>Orçamento Fornecedor</option></select></div>
                    <div><label style={lbl}>CÓDIGO FORNECEDOR *</label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input 
                        style={{ ...inp, flex: 1 }} 
                        value={supplierSearchTerm} 
                        onChange={e => setSupplierSearchTerm(e.target.value)}
                        onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); searchSupplierByCode(supplierSearchTerm); } }}
                        placeholder="Código..." 
                      />
                      <button onClick={() => searchSupplierByCode(supplierSearchTerm)} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>🔍</button>
                    </div>
                    </div>
                    <div><label style={lbl}>NOME FORNECEDOR</label><input style={{...inp, background: 'var(--bg-card)'}} value={suppliers.find(s => String(s.id) === String(purchaseForm.supplierId))?.name || ''} readOnly placeholder="Selecione pelo código" /></div>
                      <div><label style={lbl}>{(purchaseForm.documentType === 'Encomenda Fornecedor' || purchaseForm.documentType === 'Orçamento Fornecedor') ? 'Nº DOCUMENTO' : 'Nº FATURA'}</label><input style={{...inp, background: 'var(--bg-card)', cursor: 'not-allowed'}} value={purchaseForm.invoiceNumber || 'AUTOMÁTICO'} readOnly placeholder="Gerado ao guardar" /></div>
                      <div><label style={lbl}>DATA FATURA</label><input style={inp} type="date" value={purchaseForm.invoiceDate} onChange={e => setPurchaseForm(f => ({ ...f, invoiceDate: e.target.value }))} /></div>
                      <div><label style={lbl}>DATA VENCIMENTO</label><input style={inp} type="date" value={purchaseForm.dueDate} onChange={e => setPurchaseForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
                      <div><label style={lbl}>REF. FORNECEDOR</label><input style={inp} value={purchaseForm.supplierReference} onChange={e => setPurchaseForm(f => ({ ...f, supplierReference: e.target.value }))} placeholder="Ref externa" /></div>
                      <div><label style={lbl}>ARMAZÉM</label><select style={inp} value={purchaseForm.warehouseId} onChange={e => setPurchaseForm(f => ({ ...f, warehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                      <div><label style={lbl}>MÉTODO PAG.</label><select style={inp} value={purchaseForm.paymentMethod} onChange={e => setPurchaseForm(f => ({ ...f, paymentMethod: e.target.value }))}><option value="">Selecione...</option>{paymentMethods.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}</select></div>
                      <div><label style={lbl}>DESC. GLOBAL %</label><input style={inp} type="number" step="0.01" value={purchaseForm.globalDiscountPercent} onChange={e => setPurchaseForm(f => ({ ...f, globalDiscountPercent: e.target.value }))} /></div>
                    </div>
                    <div style={{ marginBottom: '10px' }}><label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}><input type="checkbox" checked={purchaseForm.autoUpdateStock} onChange={e => setPurchaseForm(f => ({ ...f, autoUpdateStock: e.target.checked }))} /> Atualizar stock automaticamente ao lançar</label></div>
                    <div><label style={lbl}>NOTAS</label><textarea style={{ ...inp, minHeight: '40px' }} value={purchaseForm.nãotes} onChange={e => setPurchaseForm(f => ({ ...f, nãotes: e.target.value }))} /></div>
                    
                    <h5 style={{ fontSize: '12px', fontWeight: '600', marginTop: '12px', marginBottom: '6px' }}>Linhas da Compra</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px 90px 70px 110px 70px 40px', gap: '4px', marginBottom: '6px', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      <div>REFERÊNCIA</div><div>DESCRIÇÃO</div><div>QTD</div><div>P.UNIT.</div><div>DESC%</div><div>TAXA IVA</div><div>TOTAL</div><div></div>
                    </div>
                    {purchaseLines.map((line, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px 90px 70px 110px 70px 40px', gap: '4px', marginBottom: '4px', alignItems: 'center', fontSize: '12px' }}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <input 
                            style={{ ...inp, fontSize: '11px', flex: 1 }} 
                            value={line.reference || ''} 
                            onChange={e => updatePurchaseLine(idx, 'reference', e.target.value)}
                            onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); searchArticleByReference(idx, line.reference); } }}
                            placeholder="Código"
                          />
                          <button 
                            onClick={() => { setSearchingLineIndex(idx); setArticleSearchTerm(''); setShowArticleSearch(true); }} 
                            style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                            title="Pesquisar artigos"
                          >🔎</button>
                        </div>
                        <input 
                          id={`desc-${idx}`}
                          style={{ ...inp, fontSize: '11px' }} 
                          value={line.description || ''} 
                          onChange={e => updatePurchaseLine(idx, 'description', e.target.value)} 
                          onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`qty-${idx}`)?.focus(); } }}
                          placeholder="Descrição" 
                        />
                        <input 
                          id={`qty-${idx}`}
                          style={{ ...inp, fontSize: '11px' }} 
                          type="number" 
                          step="0.01" 
                          value={line.quantity} 
                          onChange={e => updatePurchaseLine(idx, 'quantity', e.target.value)} 
                          onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`price-${idx}`)?.focus(); } }}
                        />
                        <input 
                          id={`price-${idx}`}
                          style={{ ...inp, fontSize: '11px' }} 
                          type="number" 
                          step="0.01" 
                          value={line.unitPrice} 
                          onChange={e => updatePurchaseLine(idx, 'unitPrice', e.target.value)} 
                          onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(`disc-${idx}`)?.focus(); } }}
                        />
                        <input 
                          id={`disc-${idx}`}
                          style={{ ...inp, fontSize: '11px' }} 
                          type="number" 
                          step="0.01" 
                          value={line.discountPercent} 
                          onChange={e => updatePurchaseLine(idx, 'discountPercent', e.target.value)} 
                          onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); if (idx === purchaseLines.length - 1) { addPurchaseLine(); setTimeout(() => document.getElementById(`desc-${idx+1}`)?.focus(), 100); } else { document.getElementById(`desc-${idx+1}`)?.focus(); } } }}
                        />
                        <select style={{ ...inp, fontSize: '11px' }} value={line.vatRateId || ''} onChange={e => { const vat = vatRates.find(v => String(v.id) === String(e.target.value)); updatePurchaseLine(idx, 'vatRateId', e.target.value); if (vat) updatePurchaseLine(idx, 'vatPercent', vat.percentage); }}>
                          <option value="">Selecione...</option>
                          {vatRates.map(v => <option key={v.id} value={v.id}>{v.percentage}% - {v.description}</option>)}
                        </select>
                        <input style={{ ...inp, fontSize: '11px', background: 'var(--bg-card)' }} disabled value={(line.quantity * line.unitPrice * (1 - (line.discountPercent || 0) / 100)).toFixed(2)} />
                        <button onClick={() => removePurchaseLine(idx)} style={{ background: 'var(--error)', color: '#fff', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', height: '28px' }} title="Remover linha">🗑️</button>
                      </div>
                    ))}
                    <button onClick={addPurchaseLine} style={{ background: 'var(--bg-tertiary)', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginTop: '6px' }}>+ Linha</button>
                    
                    <div style={{ marginTop: '12px', padding: '10px', background: 'var(--info-bg)', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', marginBottom: '4px' }}>Subtotal: <strong>{purchaseLines.reduce((sum, l) => sum + (l.quantity * l.unitPrice * (1 - (l.discountPercent || 0) / 100)), 0).toFixed(2)}€</strong></div>
                      <div style={{ fontSize: '12px', marginBottom: '4px' }}>IVA: <strong>{purchaseLines.reduce((sum, l) => sum + (l.quantity * l.unitPrice * (1 - (l.discountPercent || 0) / 100) * ((l.vatPercent || 0) / 100)), 0).toFixed(2)}€</strong></div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--info-text)' }}>Total: <strong>{(purchaseLines.reduce((sum, l) => sum + (l.quantity * l.unitPrice * (1 - (l.discountPercent || 0) / 100) * (1 + (l.vatPercent || 0) / 100)), 0)).toFixed(2)}€</strong></div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button onClick={savePurchase} style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>{editingPurchase ? 'Atualizar Documento' : 'Gravar Documento'}</button>
                      <button onClick={() => { setShowPurchaseForm(false); setEditingPurchase(null); setPurchaseForm(emptyPurchase()); setPurchaseLines([]); }} style={{ flex: 1, background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stock Movements sub-tab (Lançamento de Stock Manual) */}
            {purchaseSubTab === 'stock' && (
              <div>
                <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info)', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>📦</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--info)', marginBottom: '4px' }}>Separador de Lançamento</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>Este separador destina-se apenas ao lançamento manual de movimentos de stock. Para consultar e pesquisar movimentos existentes, aceda ao separador <strong>Relatórios</strong>.</div>
                  </div>
                </div>
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: 'var(--info-text)' }}>Lançamento Manual de Stock</h4>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    Registe manualmente entradas (compras), saídas (vendas/consumo) ou transferências entre armazéns.
                  </p>
                  
                  {/* Tab buttons for Entry/Exit/Transfer */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid var(--border-color)' }}>
                    {[
                      {k:'entry',l:'Entrada',c:'var(--success)'},
                      {k:'exit',l:'Saída',c:'var(--error)'},
                      {k:'transfer',l:'Transferência',c:'var(--info)'}
                    ].map(st => (
                      <button key={st.k} onClick={() => setWarehouseSubTab(st.k)} style={{ 
                        padding: '10px 16px', border: 'none', background: warehouseSubTab === st.k ? st.c : 'transparent',
                        color: warehouseSubTab === st.k ? '#fff' : '#7f8c8d', borderRadius: '8px 8px 0 0',
                        cursor: 'pointer', fontWeight: warehouseSubTab === st.k ? '700' : '500', fontSize: '13px',
                        transition: 'all 0.2s'
                      }}>{st.l}</button>
                    ))}
                  </div>

                  {/* Entry Form */}
                  {warehouseSubTab === 'entry' && (
                    <div style={{ background: 'var(--info-bg)', padding: '16px', borderRadius: '8px' }}>
                      <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: 'var(--success)' }}>Entrada de Stock</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                          <label style={lbl}>REFERÊNCIA ARTIGO *</label>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input 
                              style={{ ...inp, flex: 1 }} 
                              value={adjustForm.reference || ''} 
                              onChange={e => setAdjustForm(f => ({ ...f, reference: e.target.value }))}
                              onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); const art = articles.find(a => a.reference?.toLowerCase() === adjustForm.reference?.toLowerCase()); if (art) { setAdjustForm(f => ({ ...f, articleId: art.id, reference: art.reference })); toast.success('Artigo selecionado: ' + art.name); } else { setShowArticleSearch(true); toast.warning('Código não encontrado. Pesquise na lista.'); } } }}
                              placeholder="Digite o código"
                            />
                            <button onClick={() => { setShowArticleSearch(true); }} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer' }}>🔎</button>
                          </div>
                          {adjustForm.articleId && articles.find(a => a.id === adjustForm.articleId) && (
                            <div style={{ fontSize: '11px', color: 'var(--success)', marginTop: '4px', fontWeight: '500' }}>✓ {articles.find(a => a.id === adjustForm.articleId).name}</div>
                          )}
                        </div>
                        <div><label style={lbl}>ARMAZÉM *</label><select style={inp} value={adjustWarehouseId} onChange={e => setAdjustWarehouseId(e.target.value)}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                        <div><label style={lbl}>QUANTIDADE *</label><input style={inp} type="number" step="0.01" value={adjustForm.quantity} onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qtd a entrar" /></div>
                      </div>
                      <div style={{ marginTop: '10px' }}><label style={lbl}>MOTIVO / OBSERVAÇÕES</label><textarea style={{ ...inp, minHeight: '50px' }} value={adjustForm.reason} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} placeholder="Ex: Compra direta sem fatura, ajuste de inventário..." /></div>
                      <button onClick={async () => { 
                        if (!adjustForm.articleId || !adjustWarehouseId || !adjustForm.quantity) { toast.error('Preencha todos os campos obrigatórios'); return; }
                        try { 
                          await api.post(`/warehouse/${adjustWarehouseId}/stock/adjust`, { ...adjustForm, movementType: 'Entry' }); 
                          toast.success('Entrada de stock registada com sucesso!'); 
                          setAdjustForm({ articleId: '', reference: '', quantity: 0, reason: '' }); 
                          setAdjustWarehouseId(null);
                          loadAll(); 
                        } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
                      }} style={{ marginTop: '12px', background: 'var(--success)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Registar Entrada</button>
                    </div>
                  )}

                  {/* Exit Form */}
                  {warehouseSubTab === 'exit' && (
                    <div style={{ background: 'var(--error-bg)', padding: '16px', borderRadius: '8px', border: '1px solid var(--error)' }}>
                      <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: 'var(--error)' }}>Saída de Stock</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                          <label style={lbl}>REFERÊNCIA ARTIGO *</label>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input 
                              style={{ ...inp, flex: 1 }} 
                              value={adjustForm.reference || ''} 
                              onChange={e => setAdjustForm(f => ({ ...f, reference: e.target.value }))}
                              onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); const art = articles.find(a => a.reference?.toLowerCase() === adjustForm.reference?.toLowerCase()); if (art) { setAdjustForm(f => ({ ...f, articleId: art.id, reference: art.reference })); toast.success('Artigo selecionado: ' + art.name); } else { setShowArticleSearch(true); toast.warning('Código não encontrado. Pesquise na lista.'); } } }}
                              placeholder="Digite o código"
                            />
                            <button onClick={() => { setShowArticleSearch(true); }} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer' }}>🔎</button>
                          </div>
                          {adjustForm.articleId && articles.find(a => a.id === adjustForm.articleId) && (
                            <div style={{ fontSize: '11px', color: 'var(--success)', marginTop: '4px', fontWeight: '500' }}>✓ {articles.find(a => a.id === adjustForm.articleId).name}</div>
                          )}
                        </div>
                        <div><label style={lbl}>ARMAZÉM *</label><select style={inp} value={adjustWarehouseId} onChange={e => setAdjustWarehouseId(e.target.value)}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                        <div><label style={lbl}>QUANTIDADE *</label><input style={inp} type="number" step="0.01" value={adjustForm.quantity} onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qtd a sair" /></div>
                      </div>
                      <div style={{ marginTop: '10px' }}><label style={lbl}>MOTIVO / OBSERVAÇÕES</label><textarea style={{ ...inp, minHeight: '50px' }} value={adjustForm.reason} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} placeholder="Ex: Venda sem fatura, consumo interno, quebra..." /></div>
                      <button onClick={async () => {
                        if (!adjustForm.articleId || !adjustWarehouseId || !adjustForm.quantity) { toast.error('Preencha todos os campos obrigatórios'); return; }
                        try {
                          await api.post(`/warehouse/${adjustWarehouseId}/stock/adjust`, { ...adjustForm, quantity: -Math.abs(parseFloat(adjustForm.quantity)), movementType: 'Exit' });
                          toast.success('Saída de stock registada com sucesso!');
                          setAdjustForm({ articleId: '', reference: '', quantity: 0, reason: '' });
                          setAdjustWarehouseId(null);
                          loadAll();
                        } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
                      }} style={{ marginTop: '12px', background: 'var(--error)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Registar Saída</button>
                    </div>
                  )}

                  {/* Transfer Form */}
                  {warehouseSubTab === 'transfer' && (
                    <div style={{ background: 'var(--info-bg)', padding: '16px', borderRadius: '8px', border: '1px solid var(--info)' }}>
                      <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: 'var(--info)' }}>Transferência Entre Armazéns</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                          <label style={lbl}>REFERÊNCIA ARTIGO *</label>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input 
                              style={{ ...inp, flex: 1 }} 
                              value={transferForm.reference || ''} 
                              onChange={e => setTransferForm(f => ({ ...f, reference: e.target.value }))}
                              onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); const art = articles.find(a => a.reference?.toLowerCase() === transferForm.reference?.toLowerCase()); if (art) { setTransferForm(f => ({ ...f, articleId: art.id, reference: art.reference })); toast.success('Artigo selecionado: ' + art.name); } else { setShowArticleSearch(true); toast.warning('Código não encontrado. Pesquise na lista.'); } } }}
                              placeholder="Digite o código"
                            />
                            <button onClick={() => { setShowArticleSearch(true); }} style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer' }}>🔎</button>
                          </div>
                          {transferForm.articleId && articles.find(a => a.id === transferForm.articleId) && (
                            <div style={{ fontSize: '11px', color: 'var(--success)', marginTop: '4px', fontWeight: '500' }}>✓ {articles.find(a => a.id === transferForm.articleId).name}</div>
                          )}
                        </div>
                        <div><label style={lbl}>DE ARMAZÉM *</label><select style={inp} value={transferForm.fromWarehouseId} onChange={e => setTransferForm(f => ({ ...f, fromWarehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                        <div><label style={lbl}>PARA ARMAZÉM *</label><select style={inp} value={transferForm.toWarehouseId} onChange={e => setTransferForm(f => ({ ...f, toWarehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                        <div><label style={lbl}>QUANTIDADE *</label><input style={inp} type="number" step="0.01" value={transferForm.quantity} onChange={e => setTransferForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qtd a transferir" /></div>
                      </div>
                      <div style={{ marginTop: '10px' }}><label style={lbl}>OBSERVAÇÕES</label><textarea style={{ ...inp, minHeight: '50px' }} value={transferForm.nãotes} onChange={e => setTransferForm(f => ({ ...f, nãotes: e.target.value }))} placeholder="Motivo da transferência..." /></div>
                      <button onClick={async () => {
                        if (!transferForm.articleId || !transferForm.fromWarehouseId || !transferForm.toWarehouseId || !transferForm.quantity) { toast.error('Preencha todos os campos obrigatórios'); return; }
                        if (transferForm.fromWarehouseId === transferForm.toWarehouseId) { toast.error('Os armazéns de origem e destino devem ser diferentes'); return; }
                        try {
                          await api.post('/warehouse/transfer', transferForm);
                          toast.success('Transferência registada com sucesso!');
                          setTransferForm({ articleId: '', reference: '', fromWarehouseId: '', toWarehouseId: '', quantity: 0, nãotes: '' });
                          loadAll();
                        } catch (e) { toast.error(e.response?.data?.message || 'Erro'); }
                      }} style={{ marginTop: '12px', background: 'var(--info)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Executar Transferência</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: VENDAS */}
        {/* ============================================ */}
        {activeTab === 'sales' && (
          <div>
            {/* New CTA to go to dedicated sales page */}
            <div style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
              borderRadius: '12px', 
              padding: '2rem', 
              marginBottom: '1.5rem',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 10px 30px rgba(40, 167, 69, 0.3)'
            }}>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: '700' }}>
                  \uD83D\uDCBC Gestão de Vendas
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>
                  Aceda à página dedicada para lançar, consultar e gerir documentos de venda aos clientes
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/sales')}
                style={{
                  padding: '1rem 2rem',
                  background: 'white',
                  color: 'var(--success)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  transition: 'transform 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Abrir Vendas \u2192
              </button>
            </div>
            
            <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', color: 'var(--success-text)', margin: 0 }}>
                \uD83D\uDCA1 <strong>Gestão de Vendas:</strong> Crie faturas, encomendas de cliente, orçamentos e outros documentos de venda. Utilize a página dedicada para uma experiência completa com todas as funcionalidades (Gravar, Anular, Duplicar, Ver, Imprimir, Editar, Devolução, Fechar, Suspender, Abrir Suspensa, Converter, Editar Layout).
              </p>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* MODALS */}
        {/* ============================================ */}

        {/* === Removed settings & reports tabs - moved to SettingsPage === */}

        {false && activeTab === 'settings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: 'var(--info-text)' }}>?? Layouts de Documentos</h2>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>Configure os layouts de impress�o para faturas, recibos e outros documentos</p>
              </div>
              <button 
                onClick={() => {
                  setLayoutForm({
                    name: '',
                    documentType: 'FT',
                    pageSize: 'A4',
                    orientation: 'Portrait',
                    marginTop: 20,
                    marginBottom: 20,
                    marginLeft: 20,
                    marginRight: 20,
                    logoUrl: '',
                    primaryColor: '#3498db',
                    fontSize: 12,
                    isDefault: false
                  });
                  setShowLayoutDesigner(true);
                }}
                style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
              >
                + Novo Layout
              </button>
            </div>

            {/* Layouts Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {layouts.map(layout => (
                <div key={layout.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                  {layout.isDefault && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'var(--success)', color: '#fff', padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600' }}>
                      PADR�O
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--info-text)', marginBottom: '4px' }}>
                      {layout.name || layout.documentType}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {layout.documentType}
                    </div>
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                    <div>?? Formato: {layout.pageSize || 'A4'}</div>
                    <div>?? Orienta��o: {layout.orientation || 'Portrait'}</div>
                    {layout.logoUrl && <div>??? Com logotipo</div>}
                  </div>

                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => {
                        setLayoutForm(layout);
                        setShowLayoutDesigner(true);
                      }}
                      style={{ flex: 1, background: 'var(--info)', color: '#fff', border: 'nãone', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                    >
                      ?? Editar
                    </button>
                    <button 
                      onClick={() => {
                        if (window.confirm('Tem certeza que deseja eliminar este layout?')) {
                          toast.success('Layout eliminado');
                        }
                      }}
                      style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      ???
                    </button>
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {layouts.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', background: 'var(--info-bg)', borderRadius: '12px', border: '2px dashed var(--border-color)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>??</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>Nenhum layout criado</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>Crie o seu primeiro layout de documento para personalizar a impress�o</div>
                  <button 
                    onClick={() => toast.info('Funcionalidade de cria��o de layout em desenvolvimento')}
                    style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    + Criar Primeiro Layout
                  </button>
                </div>
              )}
            </div>

            {/* Info Card */}
            <div style={{ marginTop: '24px', background: 'var(--info-bg)', border: '1px solid var(--info)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>??</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--info)', marginBottom: '6px' }}>Sobre Layouts</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.6' }}>
                    Os layouts permitem personalizar a apar�ncia dos seus documentos impressos. Pode definir logotipos, cores, fontes, 
                    posicionamento de campos e muito mais. Cada tipo de documento (Fatura, Recibo, etc.) pode ter o seu layout espec�fico.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {false && activeTab === 'reports' && (
          <div>
            <div style={{ display: 'flex', gap: '2px', marginBottom: '12px' }}>
              {[{k:'invoices',l:'?? Faturas'},{k:'purchases',l:'?? Compras'},{k:'stock',l:'?? Stock Movements'}].map(st => (
                <button key={st.k} onClick={() => setPurchaseSubTab(st.k)} style={{ padding: '8px 14px', border: 'nãone', borderBottom: purchaseSubTab === st.k ? '3px solid var(--info)' : '3px solid transparent', background: purchaseSubTab === st.k ? '#fff' : '#f8f9fa', color: purchaseSubTab === st.k ? '#3498db' : '#555', cursor: 'pointer', fontWeight: purchaseSubTab === st.k ? '700' : '400', fontSize: '13px' }}>{st.l}</button>
              ))}
            </div>

            {/* Invoices Report */}
            {purchaseSubTab === 'invoices' && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead><tr style={{ background: 'var(--info-bg)' }}>
                    <th style={th}>N.� Fatura</th><th style={th}>Tipo</th><th style={th}>Cliente</th>
                    <th style={th}>Data</th><th style={th}>Venc.</th><th style={th}>Total</th>
                    <th style={th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}>
                          <strong>{inv.invoiceNumber}</strong>
                          <button 
                            onClick={() => toast.info('Pesquisa de fatura: ' + inv.invoiceNumber)} 
                            style={{ marginLeft: '6px', background: 'var(--info)', color: '#fff', border: 'nãone', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                            title="Pesquisar fatura"
                          >??</button>
                        </td>
                        <td style={td}>{inv.documentType}</td>
                        <td style={td}>{inv.userName || inv.user?.name || '-'}</td>
                        <td style={td}>{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString() : '-'}</td>
                        <td style={td}>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'}</td>
                        <td style={td}><strong>{fmtMoney(inv.totalAmount)}</strong></td>
                        <td style={td}><span style={{ background: inv.status === 'Paid' ? '#27ae60' : inv.status === 'Pending' ? '#f39c12' : '#e74c3c', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>{inv.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {invoices.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>Nenhuma fatura encontrada.</p>}
              </div>
            )}

            {/* Purchases Report */}
            {purchaseSubTab === 'purchases' && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead><tr style={{ background: 'var(--info-bg)' }}>
                    <th style={th}>N.� Fatura</th><th style={th}>Fornecedor</th><th style={th}>Data</th>
                    <th style={th}>Vencimento</th><th style={th}>Total</th><th style={th}>Estado</th>
                  </tr></thead>
                  <tbody>
                    {purchases.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}>
                          <strong>{p.invoiceNumber}</strong>
                          <button 
                            onClick={() => toast.info('Pesquisa de compra: ' + p.invoiceNumber)} 
                            style={{ marginLeft: '6px', background: 'var(--info)', color: '#fff', border: 'nãone', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                            title="Pesquisar compra"
                          >??</button>
                        </td>
                        <td style={td}>{p.supplier?.name || '-'}</td>
                        <td style={td}>{p.invoiceDate ? new Date(p.invoiceDate).toLocaleDateString() : '-'}</td>
                        <td style={td}>{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-'}</td>
                        <td style={td}><strong>{fmtMoney(p.total)}</strong></td>
                        <td style={td}><span style={{ background: statusColor(p.status), color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>{statusLabel(p.status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {purchases.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>Nenhuma compra encontrada.</p>}
              </div>
            )}

            {/* Stock Movements Report */}
            {purchaseSubTab === 'stock' && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', padding: '16px 16px 0 16px' }}>?? Movimentos de Stock</h5>
                <div style={{ padding: '0 16px 16px 16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead><tr style={{ background: 'var(--info-bg)' }}>
                      <th style={th}>Data</th><th style={th}>Tipo</th><th style={th}>Artigo</th>
                      <th style={th}>Armaz�m</th><th style={th}>Qtd</th><th style={th}>Ref. Doc</th><th style={th}>Obs</th>
                    </tr></thead>
                    <tbody>
                      {stockMovements.map(m => (
                        <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}>{m.createdAt ? new Date(m.createdAt).toLocaleString() : '-'}</td>
                          <td style={td}><span style={{ background: movTypeColor(m.movementType), color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>{movTypeLabel(m.movementType)}</span></td>
                          <td style={td}>{m.article?.reference} - {m.article?.name}</td>
                          <td style={td}>{m.warehouse?.name || '-'}</td>
                          <td style={td}><strong>{m.quantity > 0 ? '+' : ''}{m.quantity}</strong></td>
                          <td style={td}>
                            {m.referenceDocument || '-'}
                            {m.referenceDocument && (
                              <button 
                                onClick={() => toast.info('Pesquisa de documento: ' + m.referenceDocument)} 
                                style={{ marginLeft: '6px', background: 'var(--info)', color: '#fff', border: 'nãone', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                title="Pesquisar documento"
                              >??</button>
                            )}
                          </td>
                          <td style={td} title={m.nãotes}>{m.nãotes ? (m.nãotes.length > 30 ? m.nãotes.substring(0, 30) + '...' : m.nãotes) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {stockMovements.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Nenhum movimento registado</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* MODALS */}
        {/* ============================================ */}

        {/* Pay Invoice Modal */}
        {showPayModal && payingInvoice && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '400px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Registar Pagamento</h3>
              <p style={{ fontSize: '13px', marginBottom: '8px' }}>Fatura: <strong>{payingInvoice.invoiceNumber}</strong> - Total: <strong>{fmtMoney(payingInvoice.totalAmount)}</strong></p>
              <div><label style={lbl}>VALOR {'�'}</label><input style={inp} type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} /></div>
              <div style={{ marginTop: '8px' }}><label style={lbl}>M�TODO</label><select style={inp} value={payMethod} onChange={e => setPayMethod(e.target.value)}><option value="">Selecione...</option>{paymentMethods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button onClick={payInvoice} style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}> Pagar</button>
                <button onClick={() => setShowPayModal(false)} style={{ flex: 1, background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Adjust Stock Modal */}
        {showAdjustForm && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '400px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}> Ajuste de Stock</h3>
              <div><label style={lbl}>ARTIGO</label><select style={inp} value={adjustForm.articleId || ''} onChange={e => setAdjustForm(f => ({ ...f, articleId: e.target.value }))}><option value="">Selecione...</option>{articles.filter(a => a.stockControl).map(a => <option key={a.id} value={a.id}>{a.reference} - {a.name}</option>)}</select></div>
              <div style={{ marginTop: '6px' }}><label style={lbl}>ARMAZ�M</label><select style={inp} value={adjustForm.warehouseId || ''} onChange={e => setAdjustForm(f => ({ ...f, warehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
              <div style={{ marginTop: '6px' }}><label style={lbl}>QUANTIDADE (+/-)</label><input style={inp} type="number" value={adjustForm.quantity || ''} onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))} /></div>
              <div style={{ marginTop: '6px' }}><label style={lbl}>MOTIVO</label><input style={inp} value={adjustForm.reason || ''} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} /></div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button onClick={doAdjust} style={{ flex: 1, background: 'var(--warning)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>Ajustar</button>
                <button onClick={() => setShowAdjustForm(false)} style={{ flex: 1, background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Transfer Stock Modal */}
        {showTransferForm && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '450px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}> Transfer�ncia de Stock</h3>
              <div><label style={lbl}>ARTIGO</label><select style={inp} value={transferForm.articleId || ''} onChange={e => setTransferForm(f => ({ ...f, articleId: e.target.value }))}><option value="">Selecione...</option>{articles.filter(a => a.stockControl).map(a => <option key={a.id} value={a.id}>{a.reference} - {a.name}</option>)}</select></div>
              <div style={{ marginTop: '6px' }}><label style={lbl}>ARMAZ�M ORIGEM</label><select style={inp} value={transferForm.fromWarehouseId || ''} onChange={e => setTransferForm(f => ({ ...f, fromWarehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
              <div style={{ marginTop: '6px' }}><label style={lbl}>ARMAZ�M DESTINO</label><select style={inp} value={transferForm.toWarehouseId || ''} onChange={e => setTransferForm(f => ({ ...f, toWarehouseId: e.target.value }))}><option value="">Selecione...</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
              <div style={{ marginTop: '6px' }}><label style={lbl}>QUANTIDADE</label><input style={inp} type="number" value={transferForm.quantity || ''} onChange={e => setTransferForm(f => ({ ...f, quantity: e.target.value }))} /></div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button onClick={doTransfer} style={{ flex: 1, background: 'var(--accent-secondary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>Transferir</button>
                <button onClick={() => setShowTransferForm(false)} style={{ flex: 1, background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Receive Goods Modal */}
        {showReceiveForm && receivingOrder && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '500px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}> Receber Mercadoria</h3>
              <p style={{ fontSize: '13px', marginBottom: '8px' }}>Encomenda: <strong>EC-{receivingOrder.orderNumber || receivingOrder.id}</strong> - Fornecedor: <strong>{receivingOrder.supplierName || receivingOrder.supplier?.name}</strong></p>
              {receiveLines.map((line, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '4px', alignItems: 'center', fontSize: '13px' }}>
                  <span style={{ flex: 2 }}>{line.articleName || line.article?.name}</span>
                  <span>Enc: {line.orderedQuantity || line.quantity}</span>
                  <input style={{ ...inp, width: '80px' }} type="number" placeholder="Recebido" value={line.receivedQuantity || ''} onChange={e => { const ls = [...receiveLines]; ls[idx].receivedQuantity = e.target.value; setReceiveLines(ls); }} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button onClick={doReceive} style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>Confirmar Rece��o</button>
                <button onClick={() => setShowReceiveForm(false)} style={{ flex: 1, background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && selectedOrder && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '600px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Detalhes da Encomenda EC-{selectedOrder.orderNumber || selectedOrder.id}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', marginBottom: '12px' }}>
                <div><strong>Fornecedor:</strong> {selectedOrder.supplierName || selectedOrder.supplier?.name}</div>
                <div><strong>Armaz�m:</strong> {selectedOrder.warehouseName || selectedOrder.warehouse?.name}</div>
                <div><strong>Data:</strong> {new Date(selectedOrder.orderDate || selectedOrder.createdAt).toLocaleDateString()}</div>
                <div><strong>Estado:</strong> <span style={{ background: statusColor(selectedOrder.status), color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{statusLabel(selectedOrder.status)}</span></div>
                <div><strong>Total:</strong> {fmtMoney(selectedOrder.totalAmount)}</div>
              </div>
              {selectedOrder.lines && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--info-bg)' }}><th style={th}>Artigo</th><th style={th}>Qtd Encomendada</th><th style={th}>Qtd Recebida</th><th style={th}>Pre�o</th><th style={th}>Total</th></tr></thead>
                  <tbody>
                    {selectedOrder.lines.map((l,i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}>{l.articleName || l.article?.name || '-'}</td>
                        <td style={td}>{l.quantity}</td>
                        <td style={td}>{l.receivedQuantity || 0}</td>
                        <td style={td}>{fmtMoney(l.unitPrice)}</td>
                        <td style={td}><strong>{fmtMoney(l.quantity * l.unitPrice)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {selectedOrder.nãotes && <p style={{ marginTop: '8px', fontSize: '13px' }}><strong>Notas:</strong> {selectedOrder.nãotes}</p>}
              <button onClick={() => setSelectedOrder(false)} style={{ marginTop: '12px', background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '8px 20px', borderRadius: '8px', cursor: 'pointer' }}>Fechar</button>
            </div>
          </div>
        )}

        {/* Print Modal */}
        {showPrintModal && printInvoice && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '650px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid var(--border-color)', paddingBottom: '12px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: 'var(--info-text)' }}>??? Op��es de Impress�o</h3>
                <button onClick={() => setShowPrintModal(false)} style={{ background: 'nãone', border: 'nãone', fontSize: '24px', cursor: 'pointer', color: 'var(--text-muted)' }}>?</button>
              </div>

              {/* Document Info */}
              <div style={{ background: 'var(--info-bg)', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  <strong>Documento:</strong> {printInvoice.documentType} {printInvoice.invoiceNumber}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  <strong>Cliente:</strong> {printInvoice.userName || printInvoice.user?.name || '-'}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                  <strong>Valor:</strong> {fmtMoney(printInvoice.totalAmount)}
                </div>
              </div>

              {/* Print Options */}
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: 'var(--info-text)' }}>?? Vias para impress�o</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  {/* Copies */}
                  <div>
                    <label style={{ ...lbl, marginBottom: '6px' }}>N� de Vias</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="10"
                      style={inp} 
                      value={printOptions.copies} 
                      onChange={e => setPrintOptions({...printOptions, copies: parseInt(e.target.value) || 1})}
                    />
                  </div>

                  {/* Layout */}
                  <div>
                    <label style={{ ...lbl, marginBottom: '6px' }}>Layout</label>
                    <select 
                      style={inp} 
                      value={printOptions.layoutId} 
                      onChange={e => setPrintOptions({...printOptions, layoutId: e.target.value})}
                    >
                      <option value="">Fatura RUG</option>
                      {layouts.map(l => (
                        <option key={l.id} value={l.id}>{l.name || l.documentType}</option>
                      ))}
                    </select>
                  </div>

                  {/* Printer */}
                  <div>
                    <label style={{ ...lbl, marginBottom: '6px' }}>Impressora</label>
                    <select 
                      style={inp} 
                      value={printOptions.printer} 
                      onChange={e => setPrintOptions({...printOptions, printer: e.target.value})}
                    >
                      <option value="HP LaserJet Professional M121nf MFP">HP LaserJet Professional M121nf MFP</option>
                      <option value="Microsoft Print to PDF">Microsoft Print to PDF</option>
                      <option value="Impressora Padr�o">Impressora Padr�o</option>
                    </select>
                  </div>
                </div>

                {/* Checkboxes */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={printOptions.doPrint} 
                      onChange={e => setPrintOptions({...printOptions, doPrint: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>??? Imprimir</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={printOptions.sendEmail} 
                      onChange={e => setPrintOptions({...printOptions, sendEmail: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>?? Enviar para Email</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={printOptions.exportPdf} 
                      onChange={e => setPrintOptions({...printOptions, exportPdf: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>?? Exportar para PDF</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={printOptions.sendSms} 
                      onChange={e => setPrintOptions({...printOptions, sendSms: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>?? Enviar SMS</span>
                  </label>
                </div>

                {/* SMS Template info */}
                {printOptions.sendSms && (
                  <div style={{ marginTop: '12px', padding: '10px', background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: '6px', fontSize: '12px', color: 'var(--success-text)' }}>
                    <strong>?? Template SMS:</strong> SMS Documento - Notifica��o de emiss�o
                  </div>
                )}
              </div>

              {/* Add Print Job Button */}
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button 
                  onClick={() => setShowPrintQueue(true)}
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--warning)', color: 'var(--warning)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                >
                  ?? Ver Fila de Impress�o ({printQueue.length})
                </button>
                <button 
                  onClick={() => {
                    const newJob = {
                      id: Date.nãow(),
                      documentType: printInvoice.documentType,
                      invoiceNumber: printInvoice.invoiceNumber,
                      clientName: printInvoice.clientName,
                      copies: printOptions.copies,
                      printer: printOptions.printer || 'Impressora Padr�o',
                      status: 'pending',
                      addedAt: new Date().toISOString()
                    };
                    setPrintQueue([...printQueue, newJob]);
                    toast.success('Trabalho adicionado � fila!');
                  }}
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--info)', color: 'var(--info)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                >
                  + Adicionar trabalho de impress�o
                </button>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  onClick={async () => {
                    try {
                      if (printOptions.doPrint) {
                        await printInvoiceAction(printInvoice);
                        toast.success('??? Documento enviado para impress�o');
                      }
                      if (printOptions.sendEmail) {
                        await emailInvoiceAction(printInvoice);
                      }
                      if (printOptions.exportPdf) {
                        await printInvoiceAction(printInvoice);
                      }
                      if (printOptions.sendSms) {
                        toast.success('?? SMS enviado com sucesso');
                      }
                      setShowPrintModal(false);
                    } catch (e) {
                      toast.error('Erro ao processar documento');
                    }
                  }}
                  style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'nãone', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}
                >
                  ? Confirmar
                </button>
                <button 
                  onClick={() => setShowPrintModal(false)} 
                  style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Article Search Modal */}
        {showArticleSearch && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>?? Pesquisar Artigos</h3>
              <input 
                style={{ ...inp, marginBottom: '12px', fontSize: '14px' }} 
                value={articleSearchTerm} 
                onChange={e => setArticleSearchTerm(e.target.value)} 
                placeholder="Pesquisar por c�digo, nãome ou descri��o..."
                autoFocus
              />
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--info-bg)', zIndex: 1 }}>
                    <tr>
                      <th style={{ ...th, padding: '8px' }}>Refer�ncia</th>
                      <th style={{ ...th, padding: '8px' }}>Nome</th>
                      <th style={{ ...th, padding: '8px' }}>Pre�o Compra</th>
                      <th style={{ ...th, padding: '8px' }}>Stock</th>
                      <th style={{ ...th, padding: '8px' }}>A��es</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articles
                      .filter(a => {
                        const term = articleSearchTerm.toLowerCase();
                        return !term || 
                          a.reference?.toLowerCase().includes(term) || 
                          a.name?.toLowerCase().includes(term) || 
                          a.description?.toLowerCase().includes(term);
                      })
                      .slice(0, 50)
                      .map(art => (
                        <tr key={art.id} style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }} onClick={() => selectArticleFromSearch(art)} onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ ...td, padding: '8px' }}><strong>{art.reference}</strong></td>
                          <td style={{ ...td, padding: '8px' }}>{art.name}</td>
                          <td style={{ ...td, padding: '8px' }}>{(art.purchasePriceNoVat || 0).toFixed(2)}�</td>
                          <td style={{ ...td, padding: '8px' }}>{art.currentStock || 0}</td>
                          <td style={{ ...td, padding: '8px' }}>
                            <button onClick={(e) => { e.stopPropagation(); selectArticleFromSearch(art); }} style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Selecionar</button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {articles.filter(a => {
                  const term = articleSearchTerm.toLowerCase();
                  return !term || a.reference?.toLowerCase().includes(term) || a.name?.toLowerCase().includes(term) || a.description?.toLowerCase().includes(term);
                }).length === 0 && (
                  <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Nenhum artigo encontrado</p>
                )}
              </div>
              <button onClick={() => { setShowArticleSearch(false); setSearchingLineIndex(null); setArticleSearchTerm(''); }} style={{ marginTop: '12px', background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Fechar</button>
            </div>
          </div>
        )}

        {showClientSearch && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>?? Pesquisar Clientes</h3>
              <input 
                style={{ ...inp, marginBottom: '12px', fontSize: '14px' }} 
                value={clientSearchTerm} 
                onChange={e => setClientSearchTerm(e.target.value)} 
                placeholder="Pesquisar por c�digo, nãome ou NIF..."
                autoFocus
              />
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--info-bg)', zIndex: 1 }}>
                    <tr>
                      <th style={{ ...th, padding: '8px' }}>C�digo</th>
                      <th style={{ ...th, padding: '8px' }}>Nome</th>
                      <th style={{ ...th, padding: '8px' }}>NIF</th>
                      <th style={{ ...th, padding: '8px' }}>Email</th>
                      <th style={{ ...th, padding: '8px' }}>A��es</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(u => {
                        const term = clientSearchTerm.toLowerCase();
                        return !term || 
                          String(getClientCode(u)).toLowerCase().includes(term) ||
                          u.name?.toLowerCase().includes(term) || 
                          u.nif?.toLowerCase().includes(term) ||
                          u.id?.toString().includes(term);
                      })
                      .slice(0, 50)
                      .map(u => (
                        <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }} onClick={() => { setInvoiceForm(f => ({ ...f, userId: u.id, clientPostalCode: u.postalCode || '' })); setShowClientSearch(false); setClientSearchTerm(''); toast.success(`Cliente ${u.name} selecionado`); }} onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ ...td, padding: '8px', fontWeight: '700', fontSize: '14px', color: 'var(--info-text)' }}><strong>{getClientCode(u)}</strong></td>
                          <td style={{ ...td, padding: '8px' }}>{u.name}</td>
                          <td style={{ ...td, padding: '8px' }}>{u.nif || '-'}</td>
                          <td style={{ ...td, padding: '8px' }}>{u.email}</td>
                          <td style={{ ...td, padding: '8px' }}>
                            <button onClick={(e) => { e.stopPropagation(); setInvoiceForm(f => ({ ...f, userId: u.id, clientPostalCode: u.postalCode || '' })); setShowClientSearch(false); setClientSearchTerm(''); toast.success(`Cliente ${u.name} selecionado`); }} style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Selecionar</button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {users.filter(u => {
                  const term = clientSearchTerm.toLowerCase();
                  return !term || String(getClientCode(u)).toLowerCase().includes(term) || u.name?.toLowerCase().includes(term) || u.nif?.toLowerCase().includes(term) || u.id?.toString().includes(term);
                }).length === 0 && (
                  <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Nenhum cliente encontrado</p>
                )}
              </div>
              <button onClick={() => { setShowClientSearch(false); setClientSearchTerm(''); }} style={{ marginTop: '12px', background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Fechar</button>
            </div>
          </div>
        )}

        {showSupplierSearch && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>?? Pesquisar Fornecedores</h3>
              <input 
                style={{ ...inp, marginBottom: '12px', fontSize: '14px' }} 
                value={supplierSearchTerm} 
                onChange={e => setSupplierSearchTerm(e.target.value)} 
                placeholder="Pesquisar por c�digo, nãome ou NIF..."
                autoFocus
              />
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--info-bg)', zIndex: 1 }}>
                    <tr>
                      <th style={{ ...th, padding: '8px' }}>C�digo</th>
                      <th style={{ ...th, padding: '8px' }}>Nome</th>
                      <th style={{ ...th, padding: '8px' }}>NIF</th>
                      <th style={{ ...th, padding: '8px' }}>Email</th>
                      <th style={{ ...th, padding: '8px' }}>A��es</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers
                      .filter(s => {
                        const term = supplierSearchTerm.toLowerCase();
                        return !term || 
                          String(getSupplierCode(s)).toLowerCase().includes(term) ||
                          s.name?.toLowerCase().includes(term) || 
                          s.nif?.toLowerCase().includes(term) ||
                          s.id?.toString().includes(term);
                      })
                      .slice(0, 50)
                      .map(s => (
                        <tr key={s.id} style={{ borderBottom: '1px solid var(--border-color)' }} onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ ...td, padding: '8px', fontWeight: '700', fontSize: '14px', color: 'var(--info-text)' }}><strong>{getSupplierCode(s)}</strong></td>
                          <td style={{ ...td, padding: '8px' }}>{s.name}</td>
                          <td style={{ ...td, padding: '8px' }}>{s.nif || '-'}</td>
                          <td style={{ ...td, padding: '8px' }}>{s.email || '-'}</td>
                          <td style={{ ...td, padding: '8px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setPurchaseForm(f => ({ ...f, supplierId: s.id })); 
                                  setSupplierSearchTerm(getSupplierCode(s)); 
                                  setShowSupplierSearch(false); 
                                  toast.success(`Fornecedor ${s.name} selecionado`); 
                                }} 
                                style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                Selecionar
                              </button>
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  editSupplierFn(s); 
                                  setShowSupplierSearch(false); 
                                  setSupplierSearchTerm(''); 
                                }} 
                                style={{ background: 'var(--info)', color: '#fff', border: 'nãone', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                ?? Editar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {suppliers.filter(s => {
                  const term = supplierSearchTerm.toLowerCase();
                  return !term || String(getSupplierCode(s)).toLowerCase().includes(term) || s.name?.toLowerCase().includes(term) || s.nif?.toLowerCase().includes(term) || s.id?.toString().includes(term);
                }).length === 0 && (
                  <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Nenhum fornecedor encontrado</p>
                )}
              </div>
              <button onClick={() => { setShowSupplierSearch(false); setSupplierSearchTerm(''); }} style={{ marginTop: '12px', background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}>Fechar</button>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SUPPLIER FORM MODAL */}
        {/* ============================================ */}
        {showSupplierForm && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid var(--border-color)', paddingBottom: '12px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: 'var(--info-text)' }}>
                    {editingSupplier ? '?? Editar Fornecedor' : '? Novo Fornecedor'}
                  </h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>Preencha os dados do fornecedor</p>
                </div>
                <button 
                  onClick={() => { setShowSupplierForm(false); setEditingSupplier(null); setSupplierForm(emptySupplier()); }} 
                  style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                >
                  ? Fechar
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* C�digo (ID auto-increment readonly OR manual Code field) */}
                  <div>
                    <label style={lbl}>C�DIGO *</label>
                    <input 
                      style={{ ...inp, background: editingSupplier ? '#f5f5f5' : '#fff', cursor: editingSupplier ? 'nãot-allowed' : 'text' }} 
                      value={editingSupplier ? `#${editingSupplier.id}` : (supplierForm.code || '')} 
                      onChange={e => !editingSupplier && setSupplierForm({ ...supplierForm, code: e.target.value })} 
                      readOnly={!!editingSupplier}
                      placeholder={editingSupplier ? "Auto-incremental" : "C�digo do fornecedor"}
                      title={editingSupplier ? "ID auto-incremental (n�o edit�vel)" : "C�digo manual do fornecedor"}
                    />
                  </div>

                  {/* Nome */}
                  <div>
                    <label style={lbl}>NOME *</label>
                    <input 
                      style={inp} 
                      value={supplierForm.name || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, name: e.target.value })} 
                      placeholder="Nome do fornecedor"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* NIF */}
                  <div>
                    <label style={lbl}>NIF / NIPC</label>
                    <input 
                      style={inp} 
                      value={supplierForm.nif || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, nif: e.target.value })} 
                      placeholder="N�mero de contribuinte"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={lbl}>TELEFONE</label>
                    <input 
                      style={inp} 
                      value={supplierForm.phone || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, phone: e.target.value })} 
                      placeholder="Contacto telef�nico"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* Email */}
                  <div>
                    <label style={lbl}>EMAIL</label>
                    <input 
                      type="email"
                      style={inp} 
                      value={supplierForm.email || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, email: e.target.value })} 
                      placeholder="Email de contacto"
                    />
                  </div>

                  {/* Website */}
                  <div>
                    <label style={lbl}>WEBSITE</label>
                    <input 
                      style={inp} 
                      value={supplierForm.website || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, website: e.target.value })} 
                      placeholder="www.exemplo.com"
                    />
                  </div>
                </div>

                {/* Morada */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={lbl}>MORADA</label>
                  <input 
                    style={inp} 
                    value={supplierForm.address || ''} 
                    onChange={e => setSupplierForm({ ...supplierForm, address: e.target.value })} 
                    placeholder="Rua, n�, andar..."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* C�digo Postal */}
                  <div>
                    <label style={lbl}>C�DIGO POSTAL</label>
                    <input 
                      style={inp} 
                      value={supplierForm.postalCode || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, postalCode: e.target.value })} 
                      placeholder="0000-000"
                    />
                  </div>

                  {/* Cidade */}
                  <div>
                    <label style={lbl}>CIDADE</label>
                    <input 
                      style={inp} 
                      value={supplierForm.city || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, city: e.target.value })} 
                      placeholder="Cidade"
                    />
                  </div>

                  {/* País */}
                  <div>
                    <label style={lbl}>PAÍS</label>
                    <input 
                      style={inp} 
                      value={supplierForm.country || 'Portugal'} 
                      onChange={e => setSupplierForm({ ...supplierForm, country: e.target.value })} 
                      placeholder="Portugal"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* IBAN */}
                  <div>
                    <label style={lbl}>IBAN</label>
                    <input 
                      style={inp} 
                      value={supplierForm.iban || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, iban: e.target.value })} 
                      placeholder="PT50..."
                    />
                  </div>

                  {/* Payment Terms */}
                  <div>
                    <label style={lbl}>CONDI��ES DE PAGAMENTO</label>
                    <input 
                      style={inp} 
                      value={supplierForm.paymentTerms || ''} 
                      onChange={e => setSupplierForm({ ...supplierForm, paymentTerms: e.target.value })} 
                      placeholder="Ex: 30 dias"
                    />
                  </div>
                </div>

                {/* Default Discount */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={lbl}>DESCONTO PADR�O (%)</label>
                  <input 
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    style={inp} 
                    value={supplierForm.defaultDiscount || 0} 
                    onChange={e => setSupplierForm({ ...supplierForm, defaultDiscount: parseFloat(e.target.value) || 0 })} 
                    placeholder="0.00"
                  />
                </div>

                {/* Notas */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={lbl}>NOTAS / OBSERVA��ES</label>
                  <textarea 
                    style={{ ...inp, minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }} 
                    value={supplierForm.nãotes || ''} 
                    onChange={e => setSupplierForm({ ...supplierForm, nãotes: e.target.value })} 
                    placeholder="Informa��es adicionais..."
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                <button 
                  onClick={saveSupplier}
                  disabled={!supplierForm.name}
                  style={{ 
                    flex: 1, 
                    background: supplierForm.name ? '#27ae60' : '#95a5a6', 
                    color: '#fff', 
                    border: 'nãone', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    cursor: supplierForm.name ? 'pointer' : 'nãot-allowed', 
                    fontWeight: '700', 
                    fontSize: '14px' 
                  }}
                >
                  {editingSupplier ? '?? Guardar Altera��es' : '? Criar Fornecedor'}
                </button>
                <button 
                  onClick={() => { setShowSupplierForm(false); setEditingSupplier(null); setSupplierForm(emptySupplier()); }} 
                  style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {purchaseTrackingModal.open && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '720px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: 'var(--info-text)' }}>Lote / Número de Série</h2>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>{purchaseTrackingModal.lineName} • Qtd: {purchaseTrackingModal.quantity}</p>
                </div>
                <button onClick={() => closePurchaseTrackingModal({ cancelled: true })} style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer' }}>✕</button>
              </div>

              {purchaseTrackingModal.lotEnabled && (
                <div style={{ marginBottom: '16px', background: 'var(--info-bg)', borderRadius: '8px', padding: '12px' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'var(--info-text)' }}>Dados do Lote</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={lbl}>NÚMERO DO LOTE *</label>
                      <input
                        style={inp}
                        value={purchaseTrackingModal.lotNumber}
                        onChange={e => setPurchaseTrackingModal(prev => ({ ...prev, lotNumber: e.target.value }))}
                        placeholder="Ex: LOTE-2026-001"
                      />
                    </div>
                    <div>
                      <label style={lbl}>DATA DE VALIDADE</label>
                      <input
                        style={inp}
                        type="date"
                        value={purchaseTrackingModal.expiryDate || ''}
                        onChange={e => setPurchaseTrackingModal(prev => ({ ...prev, expiryDate: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}

              {purchaseTrackingModal.serialEnabled && (
                <div style={{ marginBottom: '16px', background: 'var(--info-bg)', borderRadius: '8px', padding: '12px' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'var(--info-text)' }}>Números de Série</h3>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Indique exatamente {purchaseTrackingModal.quantity} número(s) de série (1 campo por unidade).
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {(purchaseTrackingModal.serialValues || []).map((serialValue, idx) => (
                      <div key={idx}>
                        <label style={lbl}>SÉRIE #{idx + 1}</label>
                        <input
                          style={inp}
                          value={serialValue}
                          ref={el => { purchaseSerialInputRefs.current[idx] = el; }}
                          onChange={e => updatePurchaseSerialValue(idx, e.target.value)}
                          onKeyDown={e => handlePurchaseSerialEnter(idx, e)}
                          autoFocus={idx === 0}
                          placeholder={`Ex: SN${String(idx + 1).padStart(3, '0')}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => closePurchaseTrackingModal({ cancelled: true })} style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
                <button onClick={confirmPurchaseTrackingModal} style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Confirmar</button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* LAYOUT DESIGNER MODAL */}
        {/* ============================================ */}
        {showLayoutDesigner && layoutForm && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '1200px', maxHeight: '95vh' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid var(--border-color)', paddingBottom: '12px' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: 'var(--info-text)' }}>?? Designer de Layout</h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>Personalize a apar�ncia do seu documento</p>
                </div>
                <button onClick={() => setShowLayoutDesigner(false)} style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>? Fechar</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
                {/* Left Panel - Settings */}
                <div style={{ overflowY: 'auto', maxHeight: '70vh', padding: '16px', background: 'var(--info-bg)', borderRadius: '8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: 'var(--info-text)' }}>Configura��es do Layout</h3>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={lbl}>NOME DO LAYOUT *</label>
                    <input style={inp} value={layoutForm.name || ''} onChange={e => setLayoutForm({...layoutForm, name: e.target.value})} placeholder="Ex: Fatura Padr�o" />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={lbl}>TIPO DE DOCUMENTO</label>
                    <select style={inp} value={layoutForm.documentType || 'FT'} onChange={e => setLayoutForm({...layoutForm, documentType: e.target.value})}>
                      {DOC_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                    <div>
                      <label style={lbl}>FORMATO</label>
                      <select style={inp} value={layoutForm.pageSize || 'A4'} onChange={e => setLayoutForm({...layoutForm, pageSize: e.target.value})}>
                        <option>A4</option>
                        <option>A5</option>
                        <option>Letter</option>
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>ORIENTA��O</label>
                      <select style={inp} value={layoutForm.orientation || 'Portrait'} onChange={e => setLayoutForm({...layoutForm, orientation: e.target.value})}>
                        <option value="Portrait">Vertical</option>
                        <option value="Landscape">Horizontal</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={lbl}>MARGENS (mm)</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <input style={{ ...inp, fontSize: '12px' }} type="number" value={layoutForm.marginTop || 20} onChange={e => setLayoutForm({...layoutForm, marginTop: e.target.value})} placeholder="Topo" />
                      <input style={{ ...inp, fontSize: '12px' }} type="number" value={layoutForm.marginBottom || 20} onChange={e => setLayoutForm({...layoutForm, marginBottom: e.target.value})} placeholder="Fundo" />
                      <input style={{ ...inp, fontSize: '12px' }} type="number" value={layoutForm.marginLeft || 20} onChange={e => setLayoutForm({...layoutForm, marginLeft: e.target.value})} placeholder="Esquerda" />
                      <input style={{ ...inp, fontSize: '12px' }} type="number" value={layoutForm.marginRight || 20} onChange={e => setLayoutForm({...layoutForm, marginRight: e.target.value})} placeholder="Direita" />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={lbl}>LOGOTIPO (URL)</label>
                    <input style={inp} value={layoutForm.logoUrl || ''} onChange={e => setLayoutForm({...layoutForm, logoUrl: e.target.value})} placeholder="https://..." />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={lbl}>COR PRINCIPAL</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="color" value={layoutForm.primaryColor || '#3498db'} onChange={e => setLayoutForm({...layoutForm, primaryColor: e.target.value})} style={{ width: '50px', height: '36px', border: 'nãone', borderRadius: '6px', cursor: 'pointer' }} />
                      <input style={{ ...inp, flex: 1 }} value={layoutForm.primaryColor || '#3498db'} onChange={e => setLayoutForm({...layoutForm, primaryColor: e.target.value})} />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={lbl}>TAMANHO DA FONTE</label>
                    <input style={inp} type="number" min="8" max="16" value={layoutForm.fontSize || 12} onChange={e => setLayoutForm({...layoutForm, fontSize: e.target.value})} />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={layoutForm.isDefault || false} onChange={e => setLayoutForm({...layoutForm, isDefault: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                      <span style={{ fontSize: '13px', fontWeight: '600' }}>Definir como layout padr�o</span>
                    </label>
                  </div>

                  <button 
                    onClick={async () => {
                      try {
                        if (layoutForm.id) {
                          await api.put(`/billing/layouts/${layoutForm.id}`, layoutForm);
                          toast.success('Layout atualizado!');
                        } else {
                          await api.post('/billing/layouts', layoutForm);
                          toast.success('Layout criado!');
                        }
                        setShowLayoutDesigner(false);
                        loadAll();
                      } catch (e) {
                        toast.error('Erro ao guardar layout');
                      }
                    }}
                    style={{ width: '100%', background: 'var(--success)', color: '#fff', border: 'nãone', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                  >
                    ?? Guardar Layout
                  </button>
                </div>

                {/* Right Panel - Preview */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '20px', overflowY: 'auto', maxHeight: '70vh' }}>
                  <div style={{ textAlign: 'center', marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Pr�-visualiza��o</div>
                  
                  {/* Preview of Document */}
                  <div style={{ 
                    background: 'var(--bg-card)', 
                    boxShadow: '0 0 20px rgba(0,0,0,0.1)', 
                    padding: `${layoutForm.marginTop || 20}px ${layoutForm.marginRight || 20}px ${layoutForm.marginBottom || 20}px ${layoutForm.marginLeft || 20}px`,
                    minHeight: '400px',
                    fontFamily: layoutForm.fontFamily || 'Arial',
                    fontSize: `${layoutForm.fontSize || 12}px`
                  }}>
                    {/* Logo & Header */}
                    <div style={{ borderBottom: `3px solid ${layoutForm.primaryColor || '#3498db'}`, paddingBottom: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      {layoutForm.logoUrl && (
                        <img src={layoutForm.logoUrl} alt="Logo" style={{ maxWidth: '120px', maxHeight: '60px' }} onError={(e) => e.target.style.display = 'nãone'} />
                      )}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: layoutForm.primaryColor || '#3498db' }}>{DOC_TYPES.find(d => d.value === layoutForm.documentType)?.label || 'FATURA'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>N� {layoutForm.documentType}/2026/001</div>
                      </div>
                    </div>

                    {/* Company & Client Info */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>EMITENTE</div>
                        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                          <strong>A SUA EMPRESA</strong><br />
                          NIF: 123456789<br />
                          Rua Example, 123<br />
                          1000-000 Lisboa
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>CLIENTE</div>
                        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                          <strong>Cliente Exemplo</strong><br />
                          NIF: 987654321<br />
                          Data: {new Date().toLocaleDateString()}<br />
                          Vencimento: {new Date().toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {/* Items Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                      <thead>
                        <tr style={{ background: layoutForm.primaryColor || '#3498db', color: '#fff' }}>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px' }}>Descri��o</th>
                          <th style={{ padding: '8px', textAlign: 'center', fontSize: '11px', width: '80px' }}>Qtd</th>
                          <th style={{ padding: '8px', textAlign: 'right', fontSize: '11px', width: '100px' }}>P.Unit</th>
                          <th style={{ padding: '8px', textAlign: 'right', fontSize: '11px', width: '100px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '8px', fontSize: '11px' }}>Produto/Servi�o de exemplo</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px' }}>1</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontSize: '11px' }}>100,00�</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontSize: '11px', fontWeight: '600' }}>100,00�</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Totals */}
                    <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', marginBottom: '4px' }}>Subtotal: <strong>100,00�</strong></div>
                      <div style={{ fontSize: '11px', marginBottom: '8px' }}>IVA (23%): <strong>23,00�</strong></div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: layoutForm.primaryColor || '#3498db', padding: '8px', background: 'var(--info-bg)', borderRadius: '4px', display: 'inline-block' }}>
                        TOTAL: 123,00�
                      </div>
                    </div>

                    {/* Footer */}
                    {layoutForm.footerText && (
                      <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', marginTop: '20px', fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                        {layoutForm.footerText}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* DOCUMENT PREVIEW MODAL */}
        {/* ============================================ */}
        {showDocPreview && previewDoc && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '900px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>?? Pr�-visualiza��o do Documento</h2>
                <button onClick={() => setShowDocPreview(false)} style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>? Fechar</button>
              </div>

              {/* Document Preview */}
              <div style={{ background: 'var(--info-bg)', padding: '40px', borderRadius: '8px', marginBottom: '20px', minHeight: '500px' }}>
                <div style={{ background: 'var(--bg-card)', padding: '40px', boxShadow: '0 0 20px rgba(0,0,0,0.1)', maxWidth: '700px', margin: '0 auto' }}>
                  <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <h1 style={{ fontSize: '32px', color: 'var(--info)', margin: 0 }}>{previewDoc.documentType}</h1>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>N� {previewDoc.invoiceNumber}</div>
                  </div>
                  
                  <div style={{ marginBottom: '30px' }}>
                    <strong>Cliente:</strong> {previewDoc.clientName}<br />
                    <strong>Data:</strong> {new Date(previewDoc.issueDate).toLocaleDateString()}<br />
                    <strong>Vencimento:</strong> {new Date(previewDoc.dueDate).toLocaleDateString()}
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                    <thead>
                      <tr style={{ background: 'var(--info-bg)', borderBottom: '2px solid var(--info)' }}>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Descri��o</th>
                        <th style={{ padding: '12px', textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(previewDoc.lines || []).map((line, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '12px' }}>{line.description}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>{fmtMoney(line.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ textAlign: 'right', fontSize: '20px', fontWeight: '700', color: 'var(--success)' }}>
                    TOTAL: {fmtMoney(previewDoc.totalAmount)}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => {
                    setPrintInvoice(previewDoc);
                    setShowDocPreview(false);
                    setShowPrintModal(true);
                  }}
                  style={{ background: 'var(--info)', color: '#fff', border: 'nãone', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                  ??? Continuar para Impress�o
                </button>
                <button 
                  onClick={() => setShowDocPreview(false)}
                  style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* PRINT QUEUE MODAL */}
        {/* ============================================ */}
        {showPrintQueue && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '1000px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>?? Fila de Impress�o</h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>Gerir trabalhos de impress�o pendentes</p>
                </div>
                <button onClick={() => setShowPrintQueue(false)} style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>? Fechar</button>
              </div>

              {printQueue.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--info-bg)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '64px', marginBottom: '16px' }}>??</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>Fila de impress�o vazia</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Os trabalhos de impress�o aparecem aqui quando adiciona documentos � fila</div>
                </div>
              ) : (
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--info-bg)' }}>
                        <th style={{ ...th, width: '50px' }}>#</th>
                        <th style={th}>Documento</th>
                        <th style={th}>Cliente</th>
                        <th style={th}>C�pias</th>
                        <th style={th}>Impressora</th>
                        <th style={th}>Status</th>
                        <th style={th}>A��es</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printQueue.map((job, idx) => (
                        <tr key={job.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}><strong>{idx + 1}</strong></td>
                          <td style={td}>
                            <div style={{ fontWeight: '600' }}>{job.documentType} {job.invoiceNumber}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{new Date(job.addedAt).toLocaleString()}</div>
                          </td>
                          <td style={td}>{job.clientName}</td>
                          <td style={td}>{job.copies}x</td>
                          <td style={td}>{job.printer || 'Padr�o'}</td>
                          <td style={td}>
                            <span style={{ 
                              padding: '4px 8px', 
                              borderRadius: '12px', 
                              fontSize: '11px', 
                              fontWeight: '600',
                              background: job.status === 'pending' ? '#fff3cd' : job.status === 'printing' ? '#cfe2ff' : job.status === 'completed' ? '#d1e7dd' : '#f8d7da',
                              color: job.status === 'pending' ? '#664d03' : job.status === 'printing' ? '#084298' : job.status === 'completed' ? '#0f5132' : '#842029'
                            }}>
                              {job.status === 'pending' ? '? Pendente' : job.status === 'printing' ? '??? Imprimindo' : job.status === 'completed' ? '? Conclu�do' : '? Erro'}
                            </span>
                          </td>
                          <td style={td}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {job.status === 'pending' && (
                                <button 
                                  onClick={() => {
                                    const updatedQueue = [...printQueue];
                                    updatedQueue[idx].status = 'printing';
                                    setPrintQueue(updatedQueue);
                                    setTimeout(() => {
                                      updatedQueue[idx].status = 'completed';
                                      setPrintQueue([...updatedQueue]);
                                      toast.success('Documento impresso!');
                                    }, 2000);
                                  }}
                                  style={{ background: 'var(--success)', color: '#fff', border: 'nãone', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                                >
                                  ? Imprimir
                                </button>
                              )}
                              <button 
                                onClick={() => {
                                  setPrintQueue(printQueue.filter((_, i) => i !== idx));
                                  toast.info('Trabalho removido da fila');
                                }}
                                style={{ background: 'var(--error)', color: '#fff', border: 'nãone', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                ???
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <button 
                  onClick={() => {
                    const pendingJobs = printQueue.filter(j => j.status === 'pending');
                    if (pendingJobs.length === 0) {
                      toast.warning('N�o h� trabalhos pendentes');
                      return;
                    }
                    pendingJobs.forEach((job, idx) => {
                      setTimeout(() => {
                        const updatedQueue = [...printQueue];
                        const jobIndex = updatedQueue.findIndex(j => j.id === job.id);
                        updatedQueue[jobIndex].status = 'completed';
                        setPrintQueue([...updatedQueue]);
                        if (idx === pendingJobs.length - 1) {
                          toast.success(`${pendingJobs.length} documentos impressos!`);
                        }
                      }, idx * 1000);
                    });
                  }}
                  disabled={!printQueue.some(j => j.status === 'pending')}
                  style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'nãone', padding: '12px', borderRadius: '8px', cursor: printQueue.some(j => j.status === 'pending') ? 'pointer' : 'nãot-allowed', fontWeight: '600', opacity: printQueue.some(j => j.status === 'pending') ? 1 : 0.5 }}
                >
                  ?? Imprimir Todos ({printQueue.filter(j => j.status === 'pending').length})
                </button>
                <button 
                  onClick={() => {
                    setPrintQueue(printQueue.filter(j => j.status !== 'completed'));
                    toast.success('Trabalhos conclu�dos limpos');
                  }}
                  style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'nãone', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}
                >
                  ?? Limpar Conclu�dos
                </button>
              </div>
            </div>
          </div>
        )}

        {showAiImageModal && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '980px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ margin: 0 }}>🧠 Escolher imagem por IA</h3>
                <button onClick={() => setShowAiImageModal(false)} style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Fechar</button>
              </div>
              <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}><strong>Pesquisa IA:</strong> {aiImageQuery || '-'}</p>
              {aiImageSuggestions.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>Sem resultados.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                  {aiImageSuggestions.map(img => {
                    const { full, thumb } = pickAiImageUrls(img);
                    return (
                      <button
                        key={img.id}
                        onClick={() => {
                          setArticleImageFile(null);
                          setArticleForm(f => ({ ...f, imageUrl: full || img?.url || img?.proxyUrl || '' }));
                          setShowAiImageModal(false);
                          toast.success('Imagem aplicada ao artigo');
                        }}
                        style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '6px', background: 'var(--bg-card)', cursor: 'pointer' }}
                      >
                        <img
                          src={thumb || full || '/api/billing/articles/ai/image-placeholder'}
                          alt="Sugestão IA"
                          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px' }}
                          onError={(e) => {
                            const el = e.currentTarget;
                            // Sem imagem do provider, cai no marcador do PRÓPRIO servidor. Nunca em serviços de terceiros:
                            // uma imagem de stock sem proveniência nem licença registadas não entra no produto.
                            if (el.dataset.fallback === '1') return;
                            el.dataset.fallback = '1';
                            el.src = '/api/billing/articles/ai/image-placeholder';
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {showAiNutritionModal && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth: '760px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ margin: 0 }}>🧠 Validar sugestão nutricional IA</h3>
                <button onClick={() => setShowAiNutritionModal(false)} style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
              </div>

              {!aiNutritionResult?.suggestion ? (
                <p style={{ color: 'var(--text-secondary)' }}>Sem dados para validar.</p>
              ) : (
                <div>
                  <div style={{ background: 'var(--info-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
                    <div><strong>Artigo identificado:</strong> {aiNutritionResult.productName || '-'}</div>
                    <div><strong>Confiança IA:</strong> {aiNutritionResult.confidence ?? '-'}%</div>
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--info-bg)' }}>
                        <th style={th}>Campo</th>
                        <th style={th}>Valor sugerido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Dose', `${aiNutritionResult.suggestion.servingSize || 0} ${aiNutritionResult.suggestion.servingUnit || 'g'}`],
                        ['Doses/embalagem', aiNutritionResult.suggestion.servingsPerPackage || 0],
                        ['Energia (kcal)', aiNutritionResult.suggestion.energyKcal || 0],
                        ['Proteínas (g)', aiNutritionResult.suggestion.protein || 0],
                        ['Hidratos (g)', aiNutritionResult.suggestion.carbohydrates || 0],
                        ['Açúcares (g)', aiNutritionResult.suggestion.sugars || 0],
                        ['Lípidos (g)', aiNutritionResult.suggestion.fat || 0],
                        ['Sal (g)', aiNutritionResult.suggestion.salt || 0],
                      ].map(([k, v]) => (
                        <tr key={k} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}>{k}</td>
                          <td style={td}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button onClick={() => setShowAiNutritionModal(false)} style={{ background: 'var(--bg-tertiary)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '7px', cursor: 'pointer' }}>Não validar</button>
                    <button onClick={applyAiNutritionSuggestion} style={{ background: 'var(--success)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>Validar e preencher</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

/* ============ STYLES ============ */
const lbl = { display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const inp = { width: '100%', padding: '7px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const th = { padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '12px', color: 'var(--text-primary)', borderBottom: '2px solid var(--border-color)' };
const td = { padding: '6px 10px', fontSize: '13px', verticalAlign: 'middle' };
const overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal = { background: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '90%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '12px' };
const thRow = { background: 'var(--info-bg)' };
const trStyle = { borderBottom: '1px solid var(--border-color)' };
const btnSm = { background: 'var(--info)', color: '#fff', border: 'nãone', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' };
